import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  isAuthenticated,
  getUserType,
  getCurrentUser,
  getUserName,
  getRazorpayKeyId,
  perfHubGetEntitlement,
  perfHubGetBilling,
  perfHubListCampaigns,
  perfHubCreateCampaign,
  perfHubUploadCsv,
  perfHubStartMonitoring,
  perfHubListRows,
  perfHubDashboard,
  perfHubRefresh,
  perfHubRefreshComments,
  perfHubResendRow,
  perfHubExportCsv,
  perfHubCrmList,
  perfHubCrmSave,
  perfHubCrmDelete,
  perfHubCheckoutCreateOrder,
  perfHubCheckoutVerify,
  perfHubCheckoutListPending,
  perfHubCheckoutRecover,
  perfHubCheckoutDismissUnpaid,
  perfHubCancelCampaign,
  perfHubAddCreatorRow,
  perfHubDeleteCampaign,
  perfHubPatchEconomics,
  perfHubPatchPostGuardBrief,
  API_RESOLVED_FOR_ERRORS,
} from '../api/api';
import { capturePerformanceHubTab } from '../analytics/posthog';
import { MILAN_WORKSPACE_ENABLED } from '../config/productFlags';
import PerfHubCampaignEconomics from '../components/performanceHub/PerfHubCampaignEconomics';
import PerfHubCreativeDnaPanel from '../components/performanceHub/PerfHubCreativeDnaPanel';
import PerfHubCommentIntelligencePanel from '../components/performanceHub/PerfHubCommentIntelligencePanel';
import PerfHubCreatorIntelligence from '../components/performanceHub/PerfHubCreatorIntelligence';
import PerfHubEconomicsModal from '../components/performanceHub/PerfHubEconomicsModal';
import PerfHubShortlistModal from '../components/performanceHub/PerfHubShortlistModal';
import PerfHubCreatorSelectToolbar from '../components/performanceHub/PerfHubCreatorSelectToolbar';
import PerfHubCrmTab from '../components/performanceHub/PerfHubCrmTab';
import PerfHubPostGuardPanel from '../components/performanceHub/PerfHubPostGuardPanel';
import PerfHubPostGuardBriefModal from '../components/performanceHub/PerfHubPostGuardBriefModal';
import PerfHubSectionInfo from '../components/performanceHub/PerfHubSectionInfo';
import { PERF_HUB_OVERVIEW_HELP } from '../components/performanceHub/perfHubOverviewHelp';
import { downloadTopPerformersCsv } from '../components/performanceHub/perfHubTopPerformersExport';
import { formatInr } from '../components/performanceHub/perfHubEconomicsFormat';
import {
  getCrmEntryForHandle,
  mergeShortlistTags,
  buildCrmTagsForSave,
  EMPTY_CRM_FORM,
  formatShortlistIntentLabels,
  crmEntryShortlisted,
  PERF_HUB_INTENT_TAGS,
} from '../components/performanceHub/perfHubShortlistTags';
import './BrandDashboard.css';
import './PerformanceHub.css';
import './PerformanceHub.economics.css';
import './PerformanceHub.intelligence.css';
import './PerformanceHub.postguard.css';
import './PerformanceHub.creative-dna.css';
import './PerformanceHub.comment-intelligence.css';

/** Match backend `PERF_HUB_CSV_MAX_BYTES` default (config env overrides server only). */
const PERF_HUB_CSV_MAX_BYTES = 5 * 1024 * 1024;

/** Human-readable monitoring length from a Performance Hub campaign API object. */
function formatPhMonitoringWindowLabel(c) {
  if (!c) return '';
  const unit = String(c.monitoring_unit || 'weeks').toLowerCase();
  if (unit === 'days' && c.monitoring_days != null) {
    const d = Number(c.monitoring_days);
    if (!Number.isFinite(d)) return '';
    return `${d} day${d === 1 ? '' : 's'}`;
  }
  if (c.monitoring_weeks != null) {
    const w = Number(c.monitoring_weeks);
    if (!Number.isFinite(w)) return '';
    return `${w} week${w === 1 ? '' : 's'}`;
  }
  return '';
}

/** Parse Create-wizard monitoring draft; empty/invalid → default; always in [1, max]. */
function clampPerfHubMonitoringDraft(unit, draftStr) {
  const max = unit === 'days' ? 30 : 4;
  const def = unit === 'days' ? 14 : 4;
  const t = String(draftStr ?? '').trim();
  if (t === '') return def;
  const raw = parseInt(t, 10);
  if (Number.isNaN(raw)) return def;
  return Math.min(max, Math.max(1, raw));
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'campaigns', label: 'Campaigns', icon: '📁' },
  { id: 'crm', label: 'CRM', icon: '👥' },
  { id: 'plans', label: 'Plans & Pricing', icon: '💎' },
  { id: 'billing', label: 'Billing & Usages', icon: '💳' },
];

/** Sub-views inside Campaigns (lifecycle). Data lives in Mongo `perf_hub_*` collections only. */
const CAMPAIGN_SUB_TABS = [
  { id: 'setup', label: 'Create & Setup', icon: '➕' },
  { id: 'live', label: 'Live', icon: '🔴' },
  { id: 'completed', label: 'Completed', icon: '✅' },
  { id: 'cancelled', label: 'Cancelled', icon: '⛔' },
];

const PERF_HUB_TAB_IDS = new Set(TABS.map((t) => t.id));
const PERF_HUB_SUB_IDS = new Set(CAMPAIGN_SUB_TABS.map((t) => t.id));

function parsePerfHubTab(sp) {
  const raw = sp.get('tab');
  if (!raw) return 'overview';
  const t = String(raw).toLowerCase();
  return PERF_HUB_TAB_IDS.has(t) ? t : 'overview';
}

function parsePerfHubSub(sp) {
  const raw = sp.get('sub');
  if (!raw) return 'setup';
  const s = String(raw).toLowerCase();
  return PERF_HUB_SUB_IDS.has(s) ? s : 'setup';
}

/** Mirror backend `config.py` PERF_HUB_* defaults — update both if product limits change. */
const PH_PLAN_DEFAULTS = {
  free: { maxCampaigns: 1, maxCreators: 5, exportCsv: false },
};

/** Default matches backend `PERF_HUB_CANCEL_GRACE_HOURS`; server env overrides API value in `entitlement`. */
const PERF_HUB_CANCEL_GRACE_HOURS = 6;

/** Launch pricing: “60% off” vs original list price (sale ÷ 0.4). */
const PH_LAUNCH_OFF_FRACTION = 0.6;

function perfHubOriginalListPriceInr(saleInr) {
  const payFraction = 1 - PH_LAUNCH_OFF_FRACTION;
  if (payFraction <= 0) return saleInr;
  return Math.round(saleInr / payFraction);
}

/** Paid tiers on Plans tab — sale amounts from ``entitlement.checkout_prices_inr`` when loaded. */
const PH_PAID_PLANS_BASE = [
  {
    key: 'pro',
    label: 'Pro',
    defaultSaleInr: 1000,
    maxCampaigns: 5,
    maxCreators: 250,
  },
  {
    key: 'enterprise',
    label: 'Enterprise Plan',
    defaultSaleInr: 2500,
    maxCampaigns: 15,
    maxCreators: 750,
  },
];

function buildPhPaidPlansDisplay(checkoutPricesInr) {
  const m = checkoutPricesInr && typeof checkoutPricesInr === 'object' ? checkoutPricesInr : null;
  return PH_PAID_PLANS_BASE.map((p) => {
    const raw = m ? m[p.key] : undefined;
    const n = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : p.defaultSaleInr;
    const saleInr = n > 0 ? n : p.defaultSaleInr;
    return {
      ...p,
      saleInr,
      listInr: perfHubOriginalListPriceInr(saleInr),
    };
  });
}

/** True if workspace caps are at least this catalog tier (e.g. Enterprise covers Pro). */
function perfHubEntitlementMeetsPlan(displayLimits, plan) {
  if (!displayLimits || !plan) return false;
  return (
    displayLimits.campaigns >= plan.maxCampaigns &&
    displayLimits.creators >= plan.maxCreators
  );
}

/** Format an ISO date as a short, human-readable day. Returns '' on bad input. */
function formatPerfHubRenewalDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Calendar-day span between ISO instants (for stacked-billing hints). */
function perfHubPaidWindowSpanDays(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const a = new Date(startIso).getTime();
  const b = new Date(endIso).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return 0;
  return Math.ceil((b - a) / 86400000);
}

/** More than ~one month ⇒ likely mid-cycle upgrade (unused time + new month). */
const PERF_HUB_STACKED_WINDOW_DAY_THRESHOLD = 36;

const TAB_COPY = {
  overview: {
    title: 'Overview',
    subtitle:
      'Campaign metrics, ROI (₹ per 1K reach, CPE), and a ranked leaderboard — based on synced Instagram posts.',
  },
  campaigns: {
    title: 'Campaigns',
    subtitle:
      'Create campaigns, add creators (CSV or manually), set a monitoring window, and send invites. While live, you can cancel within the grace period to free a workspace slot; after that, cancel still stops tracking but the slot stays used until your plan renews or you upgrade.',
  },
  crm: {
    title: 'CRM',
    subtitle: 'Private shortlist: handles, optional email, tags, and notes—only in Performance Hub.',
  },
  plans: {
    title: 'Plans & Pricing',
    subtitle: 'Choose Free, Pro, or Enterprise Plan. Paid tiers are monthly; yearly billing will follow.',
  },
  billing: {
    title: 'Billing & Usages',
    subtitle: 'Your Performance Hub plan, live usage against workspace caps, and recent billing activity.',
  },
};

const METRICS = [
  { key: 'likes', label: 'Likes', icon: '❤️' },
  { key: 'comments', label: 'Comments', icon: '💬' },
  { key: 'plays', label: 'Plays', icon: '▶️' },
  { key: 'reach', label: 'Reach', icon: '👁️' },
  { key: 'impressions', label: 'Impressions', icon: '📈' },
  { key: 'saved', label: 'Saved', icon: '🔖' },
  { key: 'engagement', label: 'Engagement', icon: '✨' },
];

function formatHubNumber(n) {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return new Intl.NumberFormat('en-IN').format(num);
}

/**
 * Indian-style compact number formatter for tight stat tiles.
 *   < 1,00,000  → full grouped number (e.g. 8,253, 28)
 *   1L–99.99L   → "1.77L"
 *   ≥ 1Cr       → "1.25Cr"
 * Decimals are trimmed of trailing zeros (e.g. 2,00,000 → "2L", not "2.00L").
 * Callers should set the full ``formatHubNumber`` result on the title attribute
 * so the precise value is still discoverable on hover.
 */
function formatHubNumberCompact(n) {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  const abs = Math.abs(num);
  const trimDecimals = (v, max = 2) => Number(v.toFixed(max)).toString();
  if (abs < 100000) return formatHubNumber(num);
  if (abs < 10000000) return `${trimDecimals(num / 100000, 2)}L`;
  return `${trimDecimals(num / 10000000, 2)}Cr`;
}

function formatTopPerformerShortlistStatus(crmEntry) {
  if (!crmEntryShortlisted(crmEntry)) return '—';
  const intents = (crmEntry?.tags || []).filter((t) => PERF_HUB_INTENT_TAGS.includes(t));
  if (!intents.length) return 'Saved';
  return formatShortlistIntentLabels(intents);
}

/** True when we have a synced snapshot for the table/CSV (Hiker or Meta). */
function isPerfHubRowWithSyncedMetrics(row, snap) {
  if (!row || !snap) return false;
  if (String(row.metrics_status || '').toLowerCase() === 'active') return true;
  // Legacy rows before metrics_status was added: fall back to OAuth-connected + snapshot.
  if (String(row.invite_status || '').toLowerCase() === 'connected') return true;
  return false;
}

/** Connected creators with latest snapshot + economics (independent of Creative DNA quartile). */
function buildPerfHubPerformerList(dash) {
  if (!dash?.rows?.length) return [];
  const econByRow = {};
  for (const p of dash.economics?.per_creator || []) {
    const rid = p.row_id;
    if (rid) econByRow[rid] = p;
  }
  const snaps = dash.latest_snapshots || {};
  return dash.rows
    .filter((row) => isPerfHubRowWithSyncedMetrics(row, snaps[row.id]))
    .map((row) => {
      const rid = row.id;
      const snap = snaps[rid] ? { ...snaps[rid] } : null;
      const s = snap || {};
      const postLink = (snap?.permalink || row.post_url || '').trim() || null;
      return {
        row,
        snapshot: snap,
        engagement: Number(s.engagement) || 0,
        reach: Number(s.reach) || 0,
        impressions: Number(s.impressions) || 0,
        saved: Number(s.saved) || 0,
        postLink,
        economics: econByRow[rid] || null,
      };
    });
}

function perfHubSortableNumber(val) {
  if (val == null || val === '') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function sortPerfHubPerformers(list, sortKey, crmHandleSet, intelByRowId = {}) {
  const copy = list.map((tp) => ({
    ...tp,
    inCrm: crmHandleSet.has(normPerfHubCrmHandle(tp.row?.instagram_handle)),
    intel: intelByRowId[tp.row?.id] || null,
  }));
  const tieEngagement = (a, b) => (b.engagement || 0) - (a.engagement || 0);
  const desc = (getVal) => (a, b) => {
    const na = perfHubSortableNumber(getVal(a));
    const nb = perfHubSortableNumber(getVal(b));
    if (na == null && nb == null) return tieEngagement(a, b);
    if (na == null) return 1;
    if (nb == null) return -1;
    if (na !== nb) return nb - na;
    return tieEngagement(a, b);
  };
  const asc = (getVal) => (a, b) => {
    const na = perfHubSortableNumber(getVal(a));
    const nb = perfHubSortableNumber(getVal(b));
    if (na == null && nb == null) return tieEngagement(a, b);
    if (na == null) return 1;
    if (nb == null) return -1;
    if (na !== nb) return na - nb;
    return tieEngagement(a, b);
  };
  switch (sortKey) {
    case 'reach':
      return copy.sort(desc((x) => x.reach));
    case 'impressions':
      return copy.sort(desc((x) => x.impressions));
    case 'spend':
      return copy.sort(desc((x) => x.economics?.spend_inr));
    case 'cpe':
      return copy.sort(asc((x) => x.economics?.cpe_engagement_inr));
    case 'crm':
      return copy.sort((a, b) => {
        if (a.inCrm !== b.inCrm) return a.inCrm ? 1 : -1;
        return tieEngagement(a, b);
      });
    case 'rebook':
      return copy.sort(desc((x) => x.intel?.rebook_score));
    case 'ugc':
      return copy.sort(desc((x) => x.intel?.ugc_score));
    case 'saved':
      return copy.sort(desc((x) => x.saved));
    case 'efficiency':
      return copy.sort(desc((x) => x.intel?.efficiency_score));
    case 'efficiency_cpe':
      return copy.sort(asc((x) => x.economics?.cpe_engagement_inr));
    case 'engagement':
    default:
      return copy.sort(desc((x) => x.engagement));
  }
}

function formatPhBillingEventTitle(eventType) {
  const t = String(eventType || '');
  const map = {
    subscription_checkout: 'Plan purchased',
    subscription_checkout_recovered: 'Payment confirmed',
    subscription_expired: 'Subscription ended',
    dev_plan_upgrade: 'Workspace upgraded (dev)',
  };
  return map[t] || t.replace(/_/g, ' ') || 'Activity';
}

function formatPhBillingEventWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Campaign tracking summary: public-data tracked vs OAuth-connected. */
function perfHubCreatorConnectionSummary(dash) {
  const cc = dash?.creator_connection;
  if (
    cc &&
    typeof cc.total === 'number' &&
    typeof cc.tracked === 'number'
  ) {
    return cc;
  }
  const rows = Array.isArray(dash?.rows) ? dash.rows : [];
  const total = rows.length;
  const tracked = rows.filter(
    (r) => String(r?.metrics_status || '').toLowerCase() === 'active'
  ).length;
  const connected = rows.filter(
    (r) => String(r?.invite_status || '').toLowerCase() === 'connected'
  ).length;
  return {
    total,
    tracked,
    not_tracked: Math.max(0, total - tracked),
    connected,
    not_connected: Math.max(0, total - connected),
  };
}

/** Identical bullet rows on each tier card for side-by-side comparison. */
function PerfHubPlanCompareBullets({ maxCampaigns, maxCreators, csvExport, crmIncluded = true }) {
  return (
    <ul className="ph-plan-features">
      <li>
        Up to <strong>{maxCampaigns}</strong> concurrent campaign{maxCampaigns !== 1 ? 's' : ''}
      </li>
      <li>
        Up to <strong>{formatHubNumber(maxCreators)}</strong> connected creators (workspace total)
      </li>
      <li>Add creators via CSV and email invites</li>
      <li>Magic-link Instagram connection for creators</li>
      {crmIncluded ? (
        <li>Campaign dashboard, metrics, insights &amp; CRM shortlist</li>
      ) : (
        <>
          <li>Campaign dashboard, metrics &amp; insights</li>
          <li><span className="path-card-check check-emerald">✓</span> Creator shortlist (re-book, phase 2, UGC, improving)</li>
        </>
      )}
      {csvExport ? (
        <li>CSV export of metrics &amp; creator rows</li>
      ) : (
        <li className="ph-plan-neg">CSV export — not included</li>
      )}
      <li>Daily sync &amp; manual refresh (fair use)</li>
    </ul>
  );
}

function PerfHubConnectionSummary({ dash }) {
  const cc = perfHubCreatorConnectionSummary(dash);
  return (
    <div
      className="ph-connection-summary"
      role="region"
      aria-label="Campaign tracking status"
    >
      <div className="ph-connection-summary-head">
        <span className="ph-connection-summary-title">Campaign tracking</span>
        <span className="ph-connection-summary-total">
          {cc.total} creator{cc.total !== 1 ? 's' : ''} on this campaign
        </span>
      </div>
      {cc.total === 0 ? (
        <p className="ph-connection-summary-empty">
          No creators yet—add them under <strong>Campaigns</strong> → <strong>Create &amp; Setup</strong>.
        </p>
      ) : (
        <>
          <div
            className="ph-connection-summary-bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={cc.total}
            aria-valuenow={cc.tracked ?? 0}
            aria-label={`${cc.tracked ?? 0} of ${cc.total} creators tracked`}
          >
            <span
              className="ph-connection-summary-bar-fill"
              style={{
                width: `${Math.round(((cc.tracked ?? 0) / cc.total) * 100)}%`,
              }}
            />
          </div>
          <div className="ph-connection-summary-stats">
            <span className="ph-connection-pill ph-connection-pill--ok">
              <strong>{formatHubNumber(cc.tracked ?? 0)}</strong>
              <span className="ph-connection-pill-label">Tracked</span>
            </span>
            {(cc.not_tracked ?? 0) > 0 ? (
              <span className="ph-connection-pill ph-connection-pill--pending">
                <strong>{formatHubNumber(cc.not_tracked)}</strong>
                <span className="ph-connection-pill-label">Pending fetch</span>
              </span>
            ) : null}
            {(cc.connected ?? 0) > 0 ? (
              <span className="ph-connection-pill ph-connection-pill--ok">
                <strong>{formatHubNumber(cc.connected)}</strong>
                <span className="ph-connection-pill-label">OAuth connected</span>
              </span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/** Hover copy explaining why a metric is shown as "—". */
const METRIC_UNAVAILABLE_HINT = {
  plays: 'No video / Reel posts in this campaign yet — plays are only counted for video media.',
  reach: 'Reach not available yet — refresh metrics or wait for the first sync.',
  impressions:
    'Impressions not available yet — refresh metrics or wait for the first sync.',
  saved:
    'Saves are a private metric that only the creator can see. Connect Instagram (Meta) to unlock.',
};

function PerfHubTotalsGrid({ totals, availability, gridClassName }) {
  const gridCls = gridClassName ? `ph-stats-grid ${gridClassName}` : 'ph-stats-grid';
  return (
    <div className={gridCls}>
      {METRICS.map((m) => {
        const raw = totals?.[m.key];
        // Treat the metric as unavailable when the backend explicitly says so
        // ("data not available"), so the UI shows "—" instead of a misleading
        // zero. Fall back to ``true`` when the flag is missing so existing
        // dashboards keep behaving the same.
        const isAvailable = availability ? availability[m.key] !== false : true;
        const fullValue = formatHubNumber(raw);
        const displayValue = isAvailable ? formatHubNumberCompact(raw) : '—';
        const isAbbreviated =
          isAvailable && displayValue !== fullValue && displayValue !== '—';
        const titleText = !isAvailable
          ? METRIC_UNAVAILABLE_HINT[m.key] || 'Data not available yet.'
          : isAbbreviated
            ? fullValue
            : undefined;
        return (
          <div
            key={m.key}
            className={`stat-card ph-stat-tile${!isAvailable ? ' ph-stat-tile--muted' : ''}`}
            data-accent={m.key}
          >
            <span className="ph-stat-icon-bubble" aria-hidden>
              {m.icon}
            </span>
            <div className="stat-info">
              <span className="stat-value ph-stat-value" title={titleText}>
                {displayValue}
              </span>
              <span className="stat-label ph-stat-label">{m.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PerfHubInsightsBlock({ insights }) {
  if (!insights?.length) return null;
  return (
    <div className="alert-banner info ph-insights-banner ph-insights-inline">
      <span className="alert-icon">💡</span>
      <div className="alert-content">
        <h4>Insights</h4>
        <ul className="ph-insights-list">
          {insights.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Map raw API error `detail` strings to clearer copy for the hub UI. */
function formatPerfHubError(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d !== 'string') return fallback;
  if (d.startsWith('campaign_limit_reached')) {
    return (
      'Your plan allows a limited number of workspace campaign slots (draft + live lists, plus live campaigns you cancelled after the grace period). Delete a draft, complete or cancel a live list within the grace window, or upgrade under Plans & Pricing.'
    );
  }
  if (d === 'campaign_archived' || d.includes('campaign_archived')) {
    return 'This campaign is archived. Use “Restore” on the Campaigns tab to edit it or send invites again.';
  }
  if (d === 'campaign_closed' || d.includes('campaign_closed')) {
    return 'This campaign is completed or cancelled — adding creators or sending invites is no longer allowed.';
  }
  if (d === 'CRM requires paid Performance Hub plan' || d.includes('CRM requires paid')) {
    return 'CRM is included on paid Performance Hub plans. Open Plans & Pricing to upgrade.';
  }
  return d || fallback;
}

function campaignStatusLabel(status) {
  const s = (status || '').toLowerCase();
  if (s === 'monitoring') return 'live';
  return s || '—';
}

/** True if cancelling this campaign frees a workspace slot (draft, or live within grace hours). */
function perfHubCancelFreesWorkspaceSlot(campaign, graceHours) {
  const gh = Number(graceHours);
  const g = Number.isFinite(gh) && gh > 0 ? gh : PERF_HUB_CANCEL_GRACE_HOURS;
  const st = String(campaign?.status || '').toLowerCase();
  if (st === 'draft') return true;
  if (st !== 'monitoring') return true;
  const raw = campaign?.monitoring_started_at;
  if (!raw) return true;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return true;
  const elapsedHours = (Date.now() - t) / (1000 * 60 * 60);
  return elapsedHours < g;
}

/** Overview default: live first, then completed/archived, then cancelled. Draft-only lists stay unselected. */
function pickDefaultOverviewCampaignId(campaignsList) {
  const list = campaignsList || [];
  const live = list.filter((c) => (c.status || '').toLowerCase() === 'monitoring');
  if (live.length) return live[0].id;
  const done = list.filter((c) => {
    const s = (c.status || '').toLowerCase();
    return s === 'completed' || s === 'archived';
  });
  if (done.length) return done[0].id;
  const cancelled = list.filter((c) => (c.status || '').toLowerCase() === 'cancelled');
  if (cancelled.length) return cancelled[0].id;
  return null;
}

/** Initial load: no HTTP response usually means the API is unreachable (wrong URL or server not running). */
function formatPerfHubLoadError(err) {
  const detail = err?.response?.data?.detail;
  if (detail != null && detail !== '') {
    return typeof detail === 'string' ? detail : JSON.stringify(detail);
  }
  const code = err?.code;
  const msg = err?.message || '';
  if (code === 'ECONNABORTED') {
    return `The API at ${API_RESOLVED_FOR_ERRORS} did not respond in time. Check that the backend is running and reachable.`;
  }
  if (msg === 'Network Error' || code === 'ERR_NETWORK') {
    return (
      `Cannot reach the Milan API (${API_RESOLVED_FOR_ERRORS}). ` +
      'Start the backend from milan_ai/backend/src (uvicorn on port 8000). On localhost, traffic should proxy via src/setupProxy.js — stop and run npm start again, then Retry.'
    );
  }
  return msg || 'Failed to load Performance Hub';
}

function normPerfHubCrmHandle(h) {
  return String(h || '')
    .trim()
    .replace(/^@/, '')
    .toLowerCase();
}

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  let cls = 'default';
  if (s === 'connected') cls = 'connected';
  else if (s === 'pending') cls = 'pending';
  else if (s === 'connecting') cls = 'connecting';
  else if (s === 'failed') cls = 'failed';
  else if (s === 'archived') cls = 'failed';
  else if (s === 'completed') cls = 'connected';
  else if (s === 'cancelled') cls = 'failed';
  else if (s === 'monitoring' || s === 'draft') cls = 'connecting';
  return <span className={`ph-status-pill ${cls}`}>{campaignStatusLabel(status)}</span>;
}

function PerformanceHub() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Set a Performance Hub-specific document title + meta description so this
   * route is SEO-distinct from the marketing Home page. We avoid pulling in
   * react-helmet to keep the dependency surface small; tags are written
   * imperatively and restored on unmount.
   */
  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      'Performance Hub — Track Instagram for your creator lists · Milan AI';
    const descContent =
      MILAN_WORKSPACE_ENABLED
        ? 'Performance Hub by Milan AI: upload your own creator list (CSV), invite via magic link, and monitor verified Instagram metrics. Separate from Milan campaigns and escrow.'
        : 'Performance Hub by Milan AI: upload your own creator list (CSV), invite via magic link, and monitor verified Instagram metrics for brands and agencies.';
    let descTag = document.querySelector('meta[name="description"]');
    const createdDesc = !descTag;
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.setAttribute('name', 'description');
      document.head.appendChild(descTag);
    }
    const prevDesc = descTag.getAttribute('content');
    descTag.setAttribute('content', descContent);

    const ogPairs = [
      ['og:title', 'Performance Hub — Instagram metrics for your roster'],
      ['og:description', descContent],
      ['og:type', 'website'],
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', 'Performance Hub — Milan AI'],
      ['twitter:description', descContent],
    ];
    const ogState = ogPairs.map(([key, value]) => {
      const isOg = key.startsWith('og:');
      const selector = isOg
        ? `meta[property="${key}"]`
        : `meta[name="${key}"]`;
      let tag = document.querySelector(selector);
      const created = !tag;
      if (!tag) {
        tag = document.createElement('meta');
        if (isOg) tag.setAttribute('property', key);
        else tag.setAttribute('name', key);
        document.head.appendChild(tag);
      }
      const prev = tag.getAttribute('content');
      tag.setAttribute('content', value);
      return { tag, prev, created };
    });

    return () => {
      document.title = prevTitle;
      if (createdDesc) {
        descTag.parentNode && descTag.parentNode.removeChild(descTag);
      } else if (prevDesc !== null) {
        descTag.setAttribute('content', prevDesc);
      }
      ogState.forEach(({ tag, prev, created }) => {
        if (created) {
          tag.parentNode && tag.parentNode.removeChild(tag);
        } else if (prev !== null) {
          tag.setAttribute('content', prev);
        }
      });
    };
  }, []);

  const tab = useMemo(() => parsePerfHubTab(searchParams), [searchParams]);
  const campaignSubTab = useMemo(() => {
    if (tab !== 'campaigns') return 'setup';
    return parsePerfHubSub(searchParams);
  }, [searchParams, tab]);

  const setTab = useCallback(
    (nextTab) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (nextTab === 'overview') {
            p.delete('tab');
            p.delete('sub');
          } else {
            p.set('tab', nextTab);
            if (nextTab !== 'campaigns') {
              p.delete('sub');
            }
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setCampaignSubTab = useCallback(
    (nextSub) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('tab', 'campaigns');
          if (nextSub === 'setup') {
            p.delete('sub');
          } else {
            p.set('sub', nextSub);
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (parsePerfHubTab(searchParams) === 'campaigns') return;
    if (!searchParams.has('sub')) return;
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('sub');
        return p;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const [loading, setLoading] = useState(true);
  const [entitlement, setEntitlement] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [crm, setCrm] = useState([]);
  const [billing, setBilling] = useState(null);
  /** idle: never fetched; loading; ok; error */
  const [billingStatus, setBillingStatus] = useState('idle');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [busy, setBusy] = useState(false);
  /** 'pro' | 'enterprise' while creating order or Razorpay modal open */
  const [planCheckoutTier, setPlanCheckoutTier] = useState(null);
  /** Pending checkout orders the user paid for but never confirmed (closed tab,
   *  flaky network, etc.). UI shows a small banner so the brand isn't stuck. */
  const [pendingCheckouts, setPendingCheckouts] = useState([]);
  const [recoveryBusyOrderId, setRecoveryBusyOrderId] = useState(null);
  const [recoveryError, setRecoveryError] = useState('');
  const [newName, setNewName] = useState('');
  const [crmForm, setCrmForm] = useState({ ...EMPTY_CRM_FORM });
  const [createMonitoringWeeks, setCreateMonitoringWeeks] = useState(4);
  const [createMonitoringUnit, setCreateMonitoringUnit] = useState('weeks');
  const [createMonitoringDays, setCreateMonitoringDays] = useState(14);
  /** Raw digits while editing (avoids clamping partial input like "2" or ""). */
  const [perfHubMonitorCountDraft, setPerfHubMonitorCountDraft] = useState('4');
  /** Staged file + rows before a campaign id exists (Create & Setup wizard only). */
  const [pendingCsvFile, setPendingCsvFile] = useState(null);
  const [pendingCreatorRows, setPendingCreatorRows] = useState([]);
  const [wizardManual, setWizardManual] = useState({
    instagram_handle: '',
    email: '',
    post_url: '',
    fee_inr: '',
  });
  const [csvInputKey, setCsvInputKey] = useState(0);
  /** Dashboard totals/insights keyed by campaign id (parallel lists + history buckets). */
  const [campaignMetricsCache, setCampaignMetricsCache] = useState({});
  const [economicsModalOpen, setEconomicsModalOpen] = useState(false);
  const [postGuardModalOpen, setPostGuardModalOpen] = useState(false);
  const [postGuardRefreshBusy, setPostGuardRefreshBusy] = useState(false);
  const [commentsRefreshBusy, setCommentsRefreshBusy] = useState(false);
  const [shortlistModalCreators, setShortlistModalCreators] = useState(null);
  /** Shared row selection for Creator Intelligence + Top performers bulk shortlist. */
  const [creatorRowSelection, setCreatorRowSelection] = useState(() => new Set());
  const [topPerformersSort, setTopPerformersSort] = useState('engagement');
  /** Campaigns tab (Live / Completed / Cancelled): creator rows per campaign id. */
  const [campaignPanelRowsById, setCampaignPanelRowsById] = useState({});
  /** Expanded “Creators” section per campaign id (same buckets as above). */
  const [campaignPanelCreatorsOpenById, setCampaignPanelCreatorsOpenById] = useState({});
  /** Per-campaign “Refresh Metrics” in progress (parallel panels). */
  const [panelMetricsRefreshBusyIds, setPanelMetricsRefreshBusyIds] = useState({});

  const perfHubCrmHandleSet = useMemo(() => {
    const s = new Set();
    for (const c of crm) {
      const h = normPerfHubCrmHandle(c.instagram_handle);
      if (h) s.add(h);
    }
    return s;
  }, [crm]);

  const economicsConfigured = Boolean(dash?.economics?.summary?.configured);

  const intelByRowId = useMemo(() => {
    const m = {};
    for (const c of dash?.creator_intelligence?.creators || []) {
      if (c.row_id) m[c.row_id] = c;
    }
    return m;
  }, [dash?.creator_intelligence]);

  const sortedTopPerformers = useMemo(() => {
    const list = buildPerfHubPerformerList(dash);
    if (!list.length) return [];
    return sortPerfHubPerformers(list, topPerformersSort, perfHubCrmHandleSet, intelByRowId);
  }, [dash, topPerformersSort, perfHubCrmHandleSet, intelByRowId]);

  useEffect(() => {
    setTopPerformersSort('engagement');
    setCreatorRowSelection(new Set());
  }, [selectedId]);

  useEffect(() => {
    if (!economicsConfigured && (topPerformersSort === 'spend' || topPerformersSort === 'cpe')) {
      setTopPerformersSort('engagement');
    }
  }, [economicsConfigured, topPerformersSort]);

  const resolveCreatorForRowId = useCallback(
    (rowId) => {
      if (!rowId) return null;
      if (intelByRowId[rowId]) return intelByRowId[rowId];
      const row = (dash?.rows || []).find((r) => r.id === rowId);
      if (!row) return null;
      return {
        row_id: row.id,
        instagram_handle: row.instagram_handle,
        post_url: row.post_url,
      };
    },
    [intelByRowId, dash?.rows]
  );

  const toggleCreatorRowSelection = useCallback((rowId) => {
    if (!rowId) return;
    setCreatorRowSelection((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const selectCreatorRowIds = useCallback((rowIds) => {
    setCreatorRowSelection(new Set((rowIds || []).filter(Boolean)));
  }, []);

  const clearCreatorRowSelection = useCallback(() => {
    setCreatorRowSelection(new Set());
  }, []);

  const getSelectedCreators = useCallback(() => {
    return [...creatorRowSelection].map(resolveCreatorForRowId).filter(Boolean);
  }, [creatorRowSelection, resolveCreatorForRowId]);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === selectedId),
    [campaigns, selectedId]
  );
  const selectedArchived = selectedCampaign?.status === 'archived';

  const downloadFilteredTopPerformersCsv = useCallback(() => {
    if (!sortedTopPerformers.length) return;
    downloadTopPerformersCsv(
      sortedTopPerformers.map((tp) => ({
        ...tp,
        shortlist_status: formatTopPerformerShortlistStatus(
          getCrmEntryForHandle(crm, tp.row?.instagram_handle),
        ),
      })),
      {
        campaignName: selectedCampaign?.name,
        sortKey: topPerformersSort,
        economicsConfigured,
      },
    );
  }, [sortedTopPerformers, crm, selectedCampaign?.name, topPerformersSort, economicsConfigured]);

  const filteredCampaigns = useMemo(() => {
    switch (campaignSubTab) {
      case 'setup':
        return campaigns.filter((c) => c.status === 'draft');
      case 'live':
        return campaigns.filter((c) => c.status === 'monitoring');
      case 'completed':
        return campaigns.filter((c) => c.status === 'completed' || c.status === 'archived');
      case 'cancelled':
        return campaigns.filter((c) => c.status === 'cancelled');
      default:
        return campaigns;
    }
  }, [campaigns, campaignSubTab]);

  const subTabCounts = useMemo(
    () => ({
      setup: campaigns.filter((c) => c.status === 'draft').length,
      live: campaigns.filter((c) => c.status === 'monitoring').length,
      completed: campaigns.filter((c) => c.status === 'completed' || c.status === 'archived').length,
      cancelled: campaigns.filter((c) => c.status === 'cancelled').length,
    }),
    [campaigns]
  );

  const monitoringCampaignCount = useMemo(
    () => campaigns.filter((c) => c.status === 'monitoring').length,
    [campaigns]
  );

  const phPaidPlansDisplay = useMemo(
    () => buildPhPaidPlansDisplay(entitlement?.checkout_prices_inr),
    [entitlement]
  );

  const phCheckoutEnabled = useMemo(
    () => entitlement?.checkout_enabled !== false,
    [entitlement]
  );

  const planTier = entitlement?.plan_tier || 'free';
  const isPro = planTier === 'paid';
  const cancelGraceHours = useMemo(
    () =>
      typeof entitlement?.cancel_grace_hours === 'number' && entitlement.cancel_grace_hours >= 0
        ? entitlement.cancel_grace_hours
        : PERF_HUB_CANCEL_GRACE_HOURS,
    [entitlement]
  );
  const displayLimits = useMemo(() => {
    if (!entitlement) return null;
    return {
      campaigns: entitlement.max_active_campaigns ?? PH_PLAN_DEFAULTS.free.maxCampaigns,
      creators: entitlement.max_connected_creators ?? PH_PLAN_DEFAULTS.free.maxCreators,
      export: !!entitlement.export_enabled,
    };
  }, [entitlement]);

  const activePaidPlanKey = useMemo(() => {
    if (!isPro || !displayLimits) return null;
    const { campaigns: c, creators: cr } = displayLimits;
    for (const p of phPaidPlansDisplay) {
      if (p.maxCampaigns === c && p.maxCreators === cr) return p.key;
    }
    return 'custom';
  }, [isPro, displayLimits, phPaidPlansDisplay]);

  const billingPlanCard = useMemo(() => {
    if (!entitlement) return null;
    if (!isPro) {
      return {
        name: 'Free',
        priceLine: '₹0',
        unit: 'No Performance Hub subscription',
        periodLine: null,
        periodSubLine: null,
        isAdminGrant: false,
      };
    }
    if (entitlement.is_admin_grant) {
      const name =
        activePaidPlanKey === 'enterprise'
          ? 'Enterprise Plan'
          : activePaidPlanKey === 'pro'
            ? 'Pro'
            : 'Paid workspace';
      return {
        name,
        priceLine: null,
        unit: 'Complimentary or admin-managed — no renewal cycle on this workspace.',
        periodLine: null,
        periodSubLine: null,
        isAdminGrant: true,
      };
    }
    const proPlan = phPaidPlansDisplay.find((p) => p.key === 'pro');
    const entPlan = phPaidPlansDisplay.find((p) => p.key === 'enterprise');
    let catalog = null;
    if (activePaidPlanKey === 'pro') catalog = proPlan;
    else if (activePaidPlanKey === 'enterprise') catalog = entPlan;
    const name = catalog ? catalog.label : 'Paid workspace';
    const priceLine =
      catalog != null ? `₹${formatHubNumber(catalog.saleInr)}` : null;
    const unit =
      catalog != null
        ? 'per month · launch offer (see Plans for list vs sale price)'
        : 'Custom limits — amounts follow your workspace agreement.';
    const start = formatPerfHubRenewalDate(entitlement.current_period_start);
    const end = formatPerfHubRenewalDate(entitlement.current_period_end);
    const spanDays = perfHubPaidWindowSpanDays(
      entitlement.current_period_start,
      entitlement.current_period_end
    );
    const looksStacked = spanDays > PERF_HUB_STACKED_WINDOW_DAY_THRESHOLD;
    let periodLine = null;
    let periodSubLine = null;
    if (start && end) {
      periodLine = `Paid access: ${start} – ${end}`;
      if (looksStacked) {
        periodSubLine =
          'Longer than one month because you upgraded while your previous paid time was still active—we keep those unused days and add a full month from your latest payment. Your new plan limits apply immediately.';
      }
    } else if (end) {
      periodLine = `Renews / ends on ${end}`;
    } else if (start) {
      periodLine = `Started ${start}`;
    }
    return {
      name,
      priceLine,
      unit,
      periodLine,
      periodSubLine,
      isAdminGrant: false,
    };
  }, [entitlement, isPro, activePaidPlanKey, phPaidPlansDisplay]);

  const billingUpgradeTargets = useMemo(() => {
    const proPlan = phPaidPlansDisplay.find((p) => p.key === 'pro');
    const entPlan = phPaidPlansDisplay.find((p) => p.key === 'enterprise');
    if (!proPlan || !entPlan || !displayLimits) return [];
    if (!isPro) {
      return [
        { tier: 'pro', plan: proPlan },
        { tier: 'enterprise', plan: entPlan },
      ];
    }
    if (perfHubEntitlementMeetsPlan(displayLimits, entPlan)) return [];
    return [{ tier: 'enterprise', plan: entPlan, isUpgrade: true }];
  }, [isPro, displayLimits, phPaidPlansDisplay]);

  const activeCampaignCount = useMemo(() => {
    if (typeof entitlement?.active_campaign_count === 'number') {
      return entitlement.active_campaign_count;
    }
    return campaigns.filter((c) => c.status === 'draft' || c.status === 'monitoring').length;
  }, [entitlement, campaigns]);

  const liveCampaignCount = useMemo(() => {
    if (typeof entitlement?.live_campaign_count === 'number') {
      return entitlement.live_campaign_count;
    }
    return campaigns.filter((c) => String(c.status || '').toLowerCase() === 'monitoring').length;
  }, [entitlement, campaigns]);

  const maxActiveCampaigns = displayLimits?.campaigns ?? PH_PLAN_DEFAULTS.free.maxCampaigns;
  const maxConnectedCreators =
    displayLimits?.creators ?? PH_PLAN_DEFAULTS.free.maxCreators;
  const connectedCreatorsUsed = useMemo(() => {
    if (typeof entitlement?.connected_creators_count === 'number') {
      return entitlement.connected_creators_count;
    }
    return 0;
  }, [entitlement]);
  const atActiveCampaignLimit =
    entitlement != null && activeCampaignCount >= maxActiveCampaigns;

  const displayTab = tab;
  const visibleTabs = TABS;
  const tabMeta = TAB_COPY[displayTab] || TAB_COPY.overview;

  useEffect(() => {
    capturePerformanceHubTab(displayTab);
  }, [displayTab]);

  const recentBillingEvents = useMemo(
    () => (Array.isArray(billing?.events) ? billing.events.slice(0, 10) : []),
    [billing]
  );

  const campaignUsagePct = useMemo(
    () =>
      Math.min(
        100,
        Math.round((liveCampaignCount / Math.max(1, maxActiveCampaigns)) * 1000) / 10
      ),
    [liveCampaignCount, maxActiveCampaigns]
  );

  const creatorsUsagePct = useMemo(
    () =>
      Math.min(
        100,
        Math.round((connectedCreatorsUsed / Math.max(1, maxConnectedCreators)) * 1000) / 10
      ),
    [connectedCreatorsUsed, maxConnectedCreators]
  );

  const showNewListWizard = campaignSubTab === 'setup' && !atActiveCampaignLimit;

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate(`/login?redirect=${encodeURIComponent('/performance')}&type=brand`);
      return;
    }
    if (getUserType() !== 'brand') {
      navigate('/creator-dashboard');
    }
  }, [navigate]);

  const loadCore = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [entRes, campRes] = await Promise.all([
        perfHubGetEntitlement(),
        perfHubListCampaigns(),
      ]);
      setEntitlement(entRes.data);
      const list = campRes.data || [];
      setCampaigns(list);
      setSelectedId((prev) => {
        if (!prev) return prev;
        return list.some((c) => c.id === prev) ? prev : null;
      });
    } catch (e) {
      setError(formatPerfHubLoadError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Look for paid-but-not-confirmed orders and silently try to finalize.
   * Runs on every PerformanceHub load. Pending rows where Razorpay shows the
   * order as not paid are abandoned automatically so we do not nag users who
   * closed checkout without paying. Only surfaces an error banner when recovery
   * fails in an unexpected way (network, mismatch, etc.).
   */
  const checkAndRecoverPerfHubCheckouts = useCallback(async () => {
    let pending = [];
    try {
      const res = await perfHubCheckoutListPending();
      pending = res?.data?.pending || [];
    } catch (_e) {
      return;
    }
    if (pending.length === 0) {
      setPendingCheckouts([]);
      setRecoveryError('');
      return;
    }
    const stillPending = [];
    let recoveredAny = false;
    let firstHardError = '';
    for (const p of pending) {
      if (!p?.razorpay_order_id) continue;
      try {
        const r = await perfHubCheckoutRecover(p.razorpay_order_id);
        if (r?.data?.recovered) {
          recoveredAny = true;
        } else if (r?.data?.dismiss_unpaid_checkout_allowed) {
          try {
            await perfHubCheckoutDismissUnpaid(p.razorpay_order_id);
          } catch (_d) {
            stillPending.push(p);
          }
        } else if (r?.data?.ready_to_finalize === false) {
          // Paid on Razorpay but capture not visible yet — keep banner for retry.
          stillPending.push(p);
        }
      } catch (e) {
        if (!firstHardError) {
          firstHardError =
            e.response?.data?.detail ||
            e.message ||
            'Could not confirm a recent payment automatically.';
        }
        stillPending.push(p);
      }
    }
    setPendingCheckouts(stillPending);
    setRecoveryError(firstHardError);
    if (recoveredAny) {
      setSuccessMessage('Confirmed an earlier payment — your plan is active now.');
      try {
        const ent = await perfHubGetEntitlement();
        setEntitlement(ent.data);
      } catch (_e) {
        /* loadCore will retry on next nav */
      }
    }
  }, []);

  const retryPerfHubCheckoutRecovery = useCallback(
    async (razorpayOrderId) => {
      if (!razorpayOrderId) return;
      setRecoveryBusyOrderId(razorpayOrderId);
      setRecoveryError('');
      try {
        const r = await perfHubCheckoutRecover(razorpayOrderId);
        if (r?.data?.recovered) {
          setPendingCheckouts((prev) =>
            prev.filter((p) => p.razorpay_order_id !== razorpayOrderId)
          );
          setSuccessMessage('Payment confirmed — your plan is active now.');
          try {
            const ent = await perfHubGetEntitlement();
            setEntitlement(ent.data);
          } catch (_e) {
            /* ignore */
          }
        } else if (r?.data?.dismiss_unpaid_checkout_allowed) {
          try {
            await perfHubCheckoutDismissUnpaid(razorpayOrderId);
            setPendingCheckouts((prev) =>
              prev.filter((p) => p.razorpay_order_id !== razorpayOrderId)
            );
            setRecoveryError('');
          } catch (e) {
            setRecoveryError(
              e.response?.data?.detail ||
                e.message ||
                'Could not clear this checkout. Try again or contact support.'
            );
          }
        } else {
          setRecoveryError(
            r?.data?.message ||
              'Razorpay does not show a captured payment for this order yet. Try again in a minute.'
          );
        }
      } catch (e) {
        setRecoveryError(
          e.response?.data?.detail ||
            e.message ||
            'Could not confirm payment. Contact support if you were charged.'
        );
      } finally {
        setRecoveryBusyOrderId(null);
      }
    },
    []
  );

  const openPerfHubPlanCheckout = useCallback(
    async (tier) => {
      const t = String(tier || '').toLowerCase();
      if (t !== 'pro' && t !== 'enterprise') return;
      if (!phCheckoutEnabled) {
        setError(
          'Paid Performance Hub checkout is temporarily unavailable. You can keep using the Free tier; contact support if you need a paid workspace.'
        );
        return;
      }
      setError('');
      setPlanCheckoutTier(t);
      try {
        const keyId = getRazorpayKeyId();
        if (!keyId) {
          setError(
            'Razorpay is not configured for this build. Set REACT_APP_RAZORPAY_KEY_ID or use localhost for test checkout.'
          );
          setPlanCheckoutTier(null);
          return;
        }
        const orderRes = await perfHubCheckoutCreateOrder({ tier: t });
        const orderData = orderRes.data;
        const razorpayKey = orderData.key_id || keyId;

        const options = {
          key: razorpayKey,
          amount: orderData.amount_paise,
          currency: orderData.currency || 'INR',
          name: 'Milan AI',
          description:
            t === 'pro'
              ? 'Performance Hub — Pro plan (monthly)'
              : 'Performance Hub — Enterprise plan (monthly)',
          order_id: orderData.razorpay_order_id,
          handler: async function (response) {
            try {
              await perfHubCheckoutVerify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              setPlanCheckoutTier(null);
              setSuccessMessage(
                t === 'pro'
                  ? 'Payment successful. Your workspace is now on the Pro plan.'
                  : 'Payment successful. Your workspace is now on the Enterprise plan.'
              );
              await loadCore();
            } catch (verifyError) {
              const msg =
                verifyError.response?.data?.detail ||
                verifyError.message ||
                'Could not confirm payment. If you were charged, contact support with your receipt.';
              setError(msg);
              setPlanCheckoutTier(null);
            }
          },
          prefill: {
            email: getCurrentUser() || '',
            name: getUserName() || '',
          },
          theme: { color: '#8b5cf6' },
          modal: {
            ondismiss: function () {
              setPlanCheckoutTier(null);
            },
          },
        };

        if (!window.Razorpay) {
          setError('Payment gateway not loaded. Please refresh the page and try again.');
          setPlanCheckoutTier(null);
          return;
        }

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } catch (err) {
        const msg =
          err.response?.data?.detail || err.message || 'Failed to start checkout.';
        setError(msg);
        setPlanCheckoutTier(null);
      }
    },
    [loadCore, phCheckoutEnabled]
  );

  const resetCreateSetupForm = useCallback(() => {
    setSelectedId(null);
    setNewName('');
    setCreateMonitoringWeeks(4);
    setCreateMonitoringUnit('weeks');
    setCreateMonitoringDays(14);
    setPerfHubMonitorCountDraft('4');
    setPendingCsvFile(null);
    setPendingCreatorRows([]);
    setWizardManual({ instagram_handle: '', email: '', post_url: '', fee_inr: '' });
    setCsvInputKey((k) => k + 1);
    setRows([]);
    setDash(null);
    setError('');
  }, []);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (!isAuthenticated() || getUserType() !== 'brand') return;
    checkAndRecoverPerfHubCheckouts();
  }, [checkAndRecoverPerfHubCheckouts]);

  useEffect(() => {
    if (tab !== 'campaigns') return;
    if (campaignSubTab === 'setup') {
      setSelectedId(null);
      setRows([]);
      setDash(null);
      return;
    }
    if (campaignSubTab === 'completed' || campaignSubTab === 'cancelled') {
      setSelectedId(null);
      setRows([]);
      setDash(null);
      return;
    }
    if (campaignSubTab === 'live') {
      setRows([]);
      setDash(null);
      return;
    }
    const ids = new Set(filteredCampaigns.map((c) => c.id));
    if (filteredCampaigns.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !ids.has(selectedId)) {
      setSelectedId(filteredCampaigns[0].id);
    }
  }, [tab, campaignSubTab, filteredCampaigns, selectedId]);

  useEffect(() => {
    if (tab !== 'campaigns') return;
    if (!['live', 'completed', 'cancelled'].includes(campaignSubTab)) return;
    const ids = filteredCampaigns.map((c) => c.id);
    if (ids.length === 0) return;
    let cancelled = false;
    (async () => {
      await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await perfHubDashboard(id, false);
            if (cancelled) return;
            setCampaignMetricsCache((prev) => ({
              ...prev,
              [id]: {
                totals: res.data?.totals || {},
                totals_availability: res.data?.totals_availability || null,
                insights: res.data?.insights || [],
                capabilities: res.data?.capabilities || null,
              },
            }));
          } catch {
            if (!cancelled) {
              setCampaignMetricsCache((prev) => ({ ...prev, [id]: { error: true } }));
            }
          }
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, campaignSubTab, filteredCampaigns]);

  useEffect(() => {
    if (tab !== 'campaigns' || !['live', 'completed', 'cancelled'].includes(campaignSubTab)) return;
    const ids = filteredCampaigns.map((c) => c.id);
    if (ids.length === 0) {
      setCampaignPanelRowsById({});
      return;
    }
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await perfHubListRows(id);
            return [id, res.data || []];
          } catch {
            return [id, []];
          }
        })
      );
      if (!cancelled) {
        setCampaignPanelRowsById(Object.fromEntries(pairs));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, campaignSubTab, filteredCampaigns]);

  useEffect(() => {
    if (displayTab !== 'overview') return;
    setSelectedId((prev) => {
      if (prev && campaigns.some((c) => c.id === prev)) {
        return prev;
      }
      return pickDefaultOverviewCampaignId(campaigns);
    });
  }, [displayTab, campaigns]);

  const loadDashboard = async (id, auto) => {
    if (!id) return;
    try {
      const res = await perfHubDashboard(id, auto);
      setDash(res.data);
      setCampaignMetricsCache((prev) => ({
        ...prev,
        [id]: {
          totals: res.data?.totals || {},
          totals_availability: res.data?.totals_availability || null,
          insights: res.data?.insights || [],
          economics: res.data?.economics || null,
          capabilities: res.data?.capabilities || null,
        },
      }));
    } catch (e) {
      setDash(null);
    }
  };

  const loadRows = async (id) => {
    if (!id) return;
    try {
      const res = await perfHubListRows(id);
      setRows(res.data || []);
    } catch (e) {
      setRows([]);
    }
  };

  const loadCrm = async () => {
    try {
      const res = await perfHubCrmList();
      const data = res.data;
      setCrm(Array.isArray(data) ? data : []);
    } catch (e) {
      setCrm([]);
      console.warn('Performance Hub CRM list failed', e?.response?.data || e?.message);
    }
  };

  const loadBilling = async () => {
    setBillingStatus('loading');
    try {
      const [billingRes, entRes] = await Promise.all([
        perfHubGetBilling(),
        perfHubGetEntitlement(),
      ]);
      setBilling(billingRes.data);
      setEntitlement(entRes.data);
      setBillingStatus('ok');
    } catch (e) {
      setBilling(null);
      setBillingStatus('error');
    }
  };

  useEffect(() => {
    if (displayTab === 'campaigns' && campaignSubTab === 'setup') {
      return;
    }
    if (displayTab === 'campaigns' && (campaignSubTab === 'completed' || campaignSubTab === 'cancelled')) {
      return;
    }
    if (displayTab === 'campaigns' && campaignSubTab === 'live') {
      return;
    }
    if (selectedId && (displayTab === 'overview' || displayTab === 'campaigns')) {
      loadDashboard(selectedId, true);
      if (displayTab === 'overview') {
        loadRows(selectedId);
      }
    } else if (!selectedId) {
      setRows([]);
      setDash(null);
    }
  }, [selectedId, displayTab, campaignSubTab]);

  useEffect(() => {
    if (displayTab === 'crm' || displayTab === 'overview') {
      loadCrm();
    }
    if (displayTab === 'billing') loadBilling();
  }, [displayTab]);

  // Banners are tied to the action that produced them — switching tabs should
  // clear them so they don't haunt unrelated pages.
  useEffect(() => {
    setError('');
    setSuccessMessage('');
  }, [displayTab, selectedId]);

  // Auto-dismiss banners so the brand isn't stuck reading the same message
  // every time they navigate. Errors stay longer than success toasts.
  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(''), 10000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const t = setTimeout(() => setSuccessMessage(''), 5000);
    return () => clearTimeout(t);
  }, [successMessage]);

  const handleCreateAndLaunch = async () => {
    const name = (newName || '').trim() || 'Instagram tracking';
    const committed = clampPerfHubMonitoringDraft(createMonitoringUnit, perfHubMonitorCountDraft);
    if (createMonitoringUnit === 'days') {
      setCreateMonitoringDays(committed);
    } else {
      setCreateMonitoringWeeks(committed);
    }
    setPerfHubMonitorCountDraft(String(committed));
    const monitoringPayload =
      createMonitoringUnit === 'days'
        ? {
            monitoring_unit: 'days',
            monitoring_days: committed,
          }
        : {
            monitoring_unit: 'weeks',
            monitoring_weeks: committed,
          };
    if (!pendingCsvFile && pendingCreatorRows.length === 0) {
      setError('Add at least one creator (CSV upload or manual row) before sending invites.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await perfHubCreateCampaign(name, monitoringPayload);
      const cid = res.data.id;
      if (pendingCsvFile) {
        await perfHubUploadCsv(cid, pendingCsvFile);
      }
      for (const row of pendingCreatorRows) {
        await perfHubAddCreatorRow(cid, {
          instagram_handle: row.instagram_handle,
          email: row.email,
          post_url: row.post_url,
          ...(row.fee_inr != null && row.fee_inr !== ''
            ? { fee_inr: Number(row.fee_inr) }
            : {}),
        });
      }
      setPendingCreatorRows([]);
      setPendingCsvFile(null);
      setCsvInputKey((k) => k + 1);
      setNewName('');
      await perfHubStartMonitoring(cid);
      await loadCore();
      setSelectedId(cid);
      setCampaignSubTab('live');
    } catch (e) {
      setError(formatPerfHubError(e, 'Could not create campaign'));
    } finally {
      setBusy(false);
    }
  };

  const handleWizardCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setPendingCsvFile(null);
      return;
    }
    if (file.size > PERF_HUB_CSV_MAX_BYTES) {
      const mb = PERF_HUB_CSV_MAX_BYTES / (1024 * 1024);
      setError(
        `CSV is too large (maximum ${mb} MB). Split your list or shorten the file, then choose again.`
      );
      e.target.value = '';
      setPendingCsvFile(null);
      return;
    }
    setError('');
    setPendingCsvFile(file);
  };

  const handleAddWizardPendingRow = (e) => {
    e.preventDefault();
    const h = (wizardManual.instagram_handle || '').trim().replace(/^@/, '');
    const em = (wizardManual.email || '').trim();
    const url = (wizardManual.post_url || '').trim();
    const feeRaw = (wizardManual.fee_inr || '').trim().replace(/,/g, '');
    if (!h || !em || !url) {
      setError('Enter Instagram handle, email, and post URL to add a creator.');
      return;
    }
    let fee_inr;
    if (feeRaw) {
      const n = Number(feeRaw);
      if (!Number.isFinite(n) || n < 0) {
        setError('Creator cost must be a non-negative number in INR, or leave it blank.');
        return;
      }
      fee_inr = n;
    }
    setError('');
    setPendingCreatorRows((rows) => [
      ...rows,
      { instagram_handle: h, email: em, post_url: url, ...(fee_inr != null ? { fee_inr } : {}) },
    ]);
    setWizardManual({ instagram_handle: '', email: '', post_url: '', fee_inr: '' });
  };

  const handleDeletePerfHubCampaign = async (campaignId) => {
    if (
      !window.confirm(
        'Permanently delete this campaign and all creator rows and metrics? This cannot be undone.'
      )
    ) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      await perfHubDeleteCampaign(campaignId);
      await loadCore();
      if (selectedId === campaignId) {
        setSelectedId(null);
      }
    } catch (e) {
      setError(formatPerfHubError(e, 'Could not delete campaign'));
    } finally {
      setBusy(false);
    }
  };

  const handleCancelCampaignFlow = async (campaignId) => {
    const cid = campaignId ?? selectedId;
    const camp = campaigns.find((c) => c.id === cid);
    if (!cid || !camp || !['draft', 'monitoring'].includes(camp.status)) return;
    const freesSlot = perfHubCancelFreesWorkspaceSlot(camp, cancelGraceHours);
    const gh = cancelGraceHours;
    const confirmBody =
      camp.status === 'draft'
        ? 'Cancel this draft campaign? It will move to Cancelled and free a workspace slot.'
        : freesSlot
          ? `Cancel this live campaign? Instagram tracking stops for this list. You are still within the first ${gh} hours after going live — a workspace campaign slot will be freed so you can start another.`
          : `Cancel this live campaign? Tracking will stop, but this list has been live for more than ${gh} hours — your workspace campaign slot will NOT be freed (same as if the window finished). Continue?`;
    if (!window.confirm(`${confirmBody}\n\nYou can still open the cancelled entry under the Cancelled tab for reference.`)) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await perfHubCancelCampaign(cid);
      await loadCore();
      setCampaignSubTab('cancelled');
      const apiFreed = res?.data?.cancel_freed_workspace_slot;
      const apiGh = res?.data?.cancel_grace_hours ?? gh;
      if (apiFreed !== false) {
        setSuccessMessage('Campaign cancelled. A workspace campaign slot is now free for a new list.');
      } else {
        setSuccessMessage(
          `Campaign cancelled — tracking stopped. This list had been live for more than ${apiGh} hour${apiGh === 1 ? '' : 's'}, so the workspace slot stays counted under your plan until it renews or you upgrade.`
        );
      }
    } catch (e) {
      setError(formatPerfHubError(e, 'Could not cancel'));
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    if (!selectedId) return;
    setBusy(true);
    setError('');
    setSuccessMessage('');
    try {
      const res = await perfHubRefresh(selectedId);
      const data = res.data || {};
      const synced = Number(data.synced_rows) || 0;
      const skippedCap = Number(data.skipped_cap_rows) || 0;
      await loadDashboard(selectedId, false);
      if (synced > 0) {
        setSuccessMessage(
          data.message || `Updated metrics for ${synced} creator(s).`
        );
      } else if (skippedCap > 0) {
        setError(
          data.message ||
            'Metrics were not re-fetched — you already refreshed within the last 24 hours. Showing your last saved numbers.'
        );
      } else if (data.message) {
        setError(data.message);
      }
    } catch (e) {
      setError(formatPerfHubError(e, 'Refresh failed (cooldown?)'));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEconomics = async ({ budget_inr, row_fees }) => {
    if (!selectedId) return;
    setBusy(true);
    setError('');
    try {
      const res = await perfHubPatchEconomics(selectedId, { budget_inr, row_fees });
      setDash(res.data);
      setCampaignMetricsCache((prev) => ({
        ...prev,
        [selectedId]: {
          totals: res.data?.totals || {},
          totals_availability: res.data?.totals_availability || null,
          insights: res.data?.insights || [],
          economics: res.data?.economics || null,
          capabilities: res.data?.capabilities || null,
        },
      }));
      if (displayTab === 'overview') {
        await loadRows(selectedId);
      }
      setEconomicsModalOpen(false);
    } catch (e) {
      setError(formatPerfHubError(e, 'Could not save campaign spend'));
    } finally {
      setBusy(false);
    }
  };

  const handleSavePostGuardBrief = async (brief) => {
    if (!selectedId) return;
    setBusy(true);
    setError('');
    try {
      const res = await perfHubPatchPostGuardBrief(selectedId, brief);
      setDash(res.data);
      setPostGuardModalOpen(false);
      setSuccessMessage('Post Guard rules saved.');
    } catch (e) {
      setError(formatPerfHubError(e, 'Could not save Post Guard rules'));
    } finally {
      setBusy(false);
    }
  };

  const handlePostGuardInstagramRefresh = async () => {
    if (!selectedId || postGuardRefreshBusy) return;
    setPostGuardRefreshBusy(true);
    setError('');
    try {
      if (selectedCampaign?.status === 'monitoring') {
        const res = await perfHubRefresh(selectedId);
        const data = res.data || {};
        const synced = Number(data.synced_rows) || 0;
        const skippedCap = Number(data.skipped_cap_rows) || 0;
        await loadDashboard(selectedId, false);
        if (displayTab === 'overview') {
          await loadRows(selectedId);
        }
        if (synced > 0) {
          setSuccessMessage(
            data.message ||
              'Instagram data refreshed. Post Guard and overview metrics are updated.'
          );
        } else if (skippedCap > 0) {
          setError(
            data.message ||
              'Metrics were not re-fetched — refresh again after 24 hours. Showing last saved numbers.'
          );
        } else {
          setSuccessMessage(
            data.message ||
              'Instagram data refreshed. Post Guard and overview metrics are updated.'
          );
        }
        return;
      }
      await loadDashboard(selectedId, false);
      if (displayTab === 'overview') {
        await loadRows(selectedId);
      }
      setSuccessMessage('Instagram data refreshed. Post Guard and overview metrics are updated.');
    } catch (e) {
      setError(formatPerfHubError(e, 'Instagram refresh failed (cooldown?)'));
    } finally {
      setPostGuardRefreshBusy(false);
    }
  };

  const handleRefreshComments = async () => {
    if (!selectedId || commentsRefreshBusy || selectedCampaign?.status !== 'monitoring') return;
    setCommentsRefreshBusy(true);
    setError('');
    try {
      const res = await perfHubRefreshComments(selectedId);
      const ci = res.data?.comment_intelligence;
      setDash((prev) => {
        if (!prev || !ci) return prev;
        const oldCommentLines = new Set(prev.comment_intelligence?.insights || []);
        const commentLines = ci.insights || [];
        const seen = new Set();
        const insights = [];
        for (const line of [...commentLines, ...(prev.insights || [])]) {
          if (!line || seen.has(line)) continue;
          if (oldCommentLines.has(line) && !commentLines.includes(line)) continue;
          seen.add(line);
          insights.push(line);
          if (insights.length >= 10) break;
        }
        return { ...prev, comment_intelligence: ci, insights };
      });
      setSuccessMessage('Comments refreshed and Comment Intelligence updated.');
    } catch (e) {
      setError(formatPerfHubError(e, 'Could not refresh comments'));
    } finally {
      setCommentsRefreshBusy(false);
    }
  };

  const handlePanelMetricsRefresh = async (campaignId, campaignStatus) => {
    if (!campaignId || panelMetricsRefreshBusyIds[campaignId]) return;
    setPanelMetricsRefreshBusyIds((prev) => ({ ...prev, [campaignId]: true }));
    setError('');
    try {
      if (campaignStatus === 'monitoring') {
        await perfHubRefresh(campaignId);
      }
      const res = await perfHubDashboard(campaignId, false);
      setCampaignMetricsCache((prev) => ({
        ...prev,
        [campaignId]: {
          totals: res.data?.totals || {},
          totals_availability: res.data?.totals_availability || null,
          insights: res.data?.insights || [],
          economics: res.data?.economics || null,
          capabilities: res.data?.capabilities || null,
        },
      }));
    } catch (e) {
      setError(formatPerfHubError(e, 'Refresh failed (cooldown?)'));
    } finally {
      setPanelMetricsRefreshBusyIds((prev) => {
        const next = { ...prev };
        delete next[campaignId];
        return next;
      });
    }
  };

  const handleExport = async () => {
    if (!selectedId) return;
    setError('');
    try {
      const res = await perfHubExportCsv(selectedId);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (selectedCampaign?.name || 'campaign')
        .replace(/[^\w.-]+/g, '_')
        .slice(0, 48);
      a.download = `perf-hub-${safeName}-${selectedId.slice(-8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Export requires paid plan');
    }
  };

  const handleResend = async (row, liveCampaignId) => {
    setBusy(true);
    setError('');
    setSuccessMessage('');
    try {
      await perfHubResendRow(row.id);
      if (
        tab === 'campaigns' &&
        ['live', 'completed', 'cancelled'].includes(campaignSubTab) &&
        liveCampaignId
      ) {
        const res = await perfHubListRows(liveCampaignId);
        setCampaignPanelRowsById((prev) => ({ ...prev, [liveCampaignId]: res.data || [] }));
      } else if (selectedId) {
        await loadRows(selectedId);
      }
      const em = (row.email || '').trim();
      setSuccessMessage(
        em
          ? `Invite email sent to ${em}.`
          : `Invite email sent for @${row.instagram_handle}.`
      );
    } catch (e) {
      setError(formatPerfHubError(e, 'Resend failed'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (error) setSuccessMessage('');
  }, [error]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const t = setTimeout(() => setSuccessMessage(''), 8000);
    return () => clearTimeout(t);
  }, [successMessage]);

  const handleCrmSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const h = normPerfHubCrmHandle(crmForm.instagram_handle);
      if (!h) return;
      const existing = getCrmEntryForHandle(crm, h);
      const tags = buildCrmTagsForSave({
        existingEntry: existing,
        selectedIntents: crmForm.intents,
        customTagsString: crmForm.tags,
      });
      const campaignName = (crmForm.source_campaign_name || '').trim();
      const matchedCampaign = campaignName
        ? campaigns.find((c) => c.name === campaignName)
        : null;
      await perfHubCrmSave({
        instagram_handle: h,
        email: crmForm.email || null,
        notes: crmForm.notes,
        tags,
        source_campaign_id: matchedCampaign?.id || null,
        source_campaign_name: campaignName || null,
      });
      setCrmForm({ ...EMPTY_CRM_FORM });
      await loadCrm();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCrmRemove = async (crmId) => {
    if (!window.confirm('Remove this creator from your shortlist?')) return;
    setBusy(true);
    setError('');
    try {
      await perfHubCrmDelete(crmId);
      await loadCrm();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not remove');
    } finally {
      setBusy(false);
    }
  };

  const openShortlistForRow = (row) => {
    const c = resolveCreatorForRowId(row?.id);
    if (c) setShortlistModalCreators([c]);
  };

  const handleCommentCrmNote = async (instagramHandle, noteText) => {
    const h = normPerfHubCrmHandle(instagramHandle);
    const note = (noteText || '').trim();
    if (!h || !note) return;
    setBusy(true);
    setError('');
    const campaignLabel = selectedCampaign?.name || 'campaign';
    try {
      const existing = getCrmEntryForHandle(crm, h);
      const block = note.startsWith('[') ? note : `[${campaignLabel}] ${note}`;
      const notes = existing?.notes?.trim() ? `${existing.notes.trim()}\n${block}` : block;
      const row = (dash?.rows || []).find(
        (r) => normPerfHubCrmHandle(r.instagram_handle) === h
      );
      await perfHubCrmSave({
        instagram_handle: h,
        email: (row?.email && String(row.email).trim()) || existing?.email || null,
        notes,
        tags: existing?.tags || [],
        source_campaign_id: selectedId || null,
        source_campaign_name: campaignLabel,
      });
      await loadCrm();
      setSuccessMessage(`CRM note saved for @${h}.`);
    } catch (e) {
      setError(formatPerfHubError(e, 'Could not save CRM note'));
    } finally {
      setBusy(false);
    }
  };

  const openBulkShortlist = () => {
    const list = getSelectedCreators();
    if (list.length) setShortlistModalCreators(list);
  };

  const handleShortlistSave = async ({ creators: toSave, intents, note }) => {
    const list = (toSave || []).filter((c) => c?.instagram_handle);
    if (!list.length) return;
    setBusy(true);
    setError('');
    const campaignLabel = selectedCampaign?.name || 'campaign';
    const tagLabel = formatShortlistIntentLabels(intents);
    let saved = 0;
    try {
      for (const creator of list) {
        const h = normPerfHubCrmHandle(creator.instagram_handle);
        if (!h) continue;
        const existing = getCrmEntryForHandle(crm, h);
        const lines = [
          `Performance Hub shortlist — ${campaignLabel}`,
          `Tags: ${tagLabel}`,
        ];
        if (note) lines.push(note);
        if (list.length === 1 && creator.ugc_score != null) {
          lines.push(`UGC score: ${creator.ugc_score}/100`);
        }
        const postLink = creator.permalink || creator.post_url;
        if (list.length === 1 && postLink) lines.push(`Post: ${postLink}`);
        const block = lines.join('\n');
        const notes = existing?.notes?.trim() ? `${existing.notes.trim()}\n${block}` : block;
        const row = (dash?.rows || []).find((r) => r.id === creator.row_id);
        const email =
          (row?.email && String(row.email).trim()) || existing?.email || null;
        const tags = mergeShortlistTags(existing?.tags, intents);
        await perfHubCrmSave({
          instagram_handle: h,
          email: email || null,
          notes,
          tags,
          source_campaign_id: selectedId || null,
          source_campaign_name: campaignLabel,
        });
        saved += 1;
      }
      await loadCrm();
      setShortlistModalCreators(null);
      setCreatorRowSelection(new Set());
      if (saved === 1) {
        const h = normPerfHubCrmHandle(list[0].instagram_handle);
        setSuccessMessage(`@${h} saved to shortlist (${tagLabel}).`);
      } else {
        setSuccessMessage(`${saved} creators saved to shortlist (${tagLabel}).`);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save shortlist');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !entitlement) {
    return (
      <div className="brand-dashboard ph-page ph-hub">
        <Navbar />
        <div className="dashboard-loading ph-hub-loading" style={{ minHeight: '70vh' }}>
          <div className="loading-spinner" aria-hidden />
          <p className="ph-loading-text">Loading Performance Hub…</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="brand-dashboard ph-page ph-hub">
      <Navbar />
      <div className="dashboard-container ph-dashboard-container">
        <aside className="dashboard-sidebar ph-sidebar-glass">
          <div className="sidebar-header">
            <h2>Performance Hub</h2>
          </div>
          <nav className="sidebar-nav">
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`nav-item ${displayTab === t.id ? 'active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                <span className="nav-icon">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </nav>
          {MILAN_WORKSPACE_ENABLED && (
            <div className="ph-nav-back">
              <Link to="/brand-dashboard" className="nav-item">
                <span className="nav-icon">←</span>
                Main dashboard
              </Link>
            </div>
          )}
          {!MILAN_WORKSPACE_ENABLED && (
            <div className="ph-nav-back">
              <Link to="/" className="nav-item">
                <span className="nav-icon">←</span>
                Home
              </Link>
            </div>
          )}
        </aside>

        <main className="dashboard-main">
          <div className="ph-main-inner tab-content overview-page">
            <div className="tab-header-row ph-header-block">
              <div>
                <span className="ph-eyebrow">Performance Hub</span>
                <h1 className="ph-hero-title ph-hero-title--gradient">{tabMeta.title}</h1>
                <p className="tab-subtitle ph-hero-subtitle">{tabMeta.subtitle}</p>
              </div>
            </div>

            {error && (
              <div className="ph-alert-error" role="alert">
                <span aria-hidden>⚠️</span>
                <div className="ph-alert-body">
                  {typeof error === 'string' ? error : JSON.stringify(error)}
                </div>
                <div className="ph-alert-actions">
                  <button
                    type="button"
                    className="btn-outline"
                    disabled={busy || loading}
                    onClick={() => loadCore()}
                  >
                    Retry
                  </button>
                </div>
                <button
                  type="button"
                  className="ph-alert-close"
                  aria-label="Dismiss this message"
                  onClick={() => setError('')}
                >
                  ×
                </button>
              </div>
            )}

            {pendingCheckouts.length > 0 && (
              <div className="ph-alert-warning" role="status" aria-live="polite">
                <span aria-hidden>⏳</span>
                <div className="ph-alert-body">
                  <strong>Confirming a recent payment.</strong>{' '}
                  If you completed checkout on Razorpay, click <em>Retry</em>. If
                  you closed the modal without paying, you can ignore this and
                  buy again from Plans &amp; Pricing.
                  {recoveryError && (
                    <div className="ph-alert-subtext">{recoveryError}</div>
                  )}
                </div>
                <div className="ph-alert-actions ph-alert-actions--stacked">
                  {pendingCheckouts.map((p) => (
                    <button
                      key={p.razorpay_order_id}
                      type="button"
                      className="btn-outline"
                      disabled={recoveryBusyOrderId === p.razorpay_order_id}
                      onClick={() => retryPerfHubCheckoutRecovery(p.razorpay_order_id)}
                    >
                      {recoveryBusyOrderId === p.razorpay_order_id
                        ? 'Checking…'
                        : `Retry ${p.tier === 'enterprise' ? 'Enterprise' : 'Pro'} payment`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {displayTab === 'overview' && (
              <>
                <section className="performance-dashboard ph-surface-hero">
                  <div className="dashboard-header ph-overview-header">
                    <div className="ph-section-head">
                      <span className="ph-section-head-icon" aria-hidden>
                        📊
                      </span>
                      <div className="ph-section-head-text">
                        <h2 className="ph-section-head-title">
                          {selectedCampaign?.name || 'Choose a campaign'}
                        </h2>
                        <div className="ph-section-head-meta">
                          {selectedCampaign ? (
                            <>
                              <StatusPill status={selectedCampaign.status} />
                              <span className="ph-section-head-meta-copy">
                                Instagram metrics &amp; shortlist signals
                              </span>
                            </>
                          ) : (
                            <span className="ph-section-head-meta-copy">
                              Select a campaign below to load totals and intelligence
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="ph-toolbar ph-toolbar--inline">
                      <button
                        type="button"
                        className="btn-primary ph-btn-glow"
                        disabled={busy || !selectedId || selectedArchived}
                        onClick={handleRefresh}
                      >
                        Refresh Metrics
                      </button>
                      <button
                        type="button"
                        className="btn-secondary ph-btn-secondary"
                        disabled={busy || !selectedId}
                        onClick={handleExport}
                      >
                        Export CSV {entitlement?.export_enabled ? '' : '(paid)'}
                      </button>
                    </div>
                  </div>
                  {selectedId && selectedArchived && (
                    <p className="ph-overview-archived-hint ph-cell-muted">
                      This campaign is archived—restore it on the Campaigns tab to refresh live metrics.
                    </p>
                  )}
                  <label className="ph-field-label" htmlFor="ph-overview-campaign">
                    Switch campaign
                  </label>
                  <select
                    id="ph-overview-campaign"
                    className="ph-select"
                    value={selectedId || ''}
                    onChange={(e) => setSelectedId(e.target.value)}
                  >
                    <option value="">Select campaign</option>
                    {campaigns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({campaignStatusLabel(c.status)})
                      </option>
                    ))}
                  </select>

                  {dash && selectedId && (
                    <>
                      <PerfHubConnectionSummary dash={dash} />
                      <PerfHubTotalsGrid
                        totals={dash.totals}
                        availability={dash.totals_availability}
                      />

                      <PerfHubCampaignEconomics
                        economics={dash?.economics}
                        onEditBudget={() => setEconomicsModalOpen(true)}
                        showEdit={!selectedArchived}
                        campaignName={selectedCampaign?.name}
                        exportEnabled={!!entitlement?.export_enabled}
                      />

                      <PerfHubCreativeDnaPanel creativeDna={dash?.creative_dna} />

                      <PerfHubCommentIntelligencePanel
                        commentIntelligence={dash?.comment_intelligence}
                        busy={busy}
                        commentsRefreshBusy={commentsRefreshBusy}
                        canRefreshComments={
                          selectedCampaign?.status === 'monitoring' && !selectedArchived
                        }
                        onRefreshComments={handleRefreshComments}
                        onAddCrmNote={handleCommentCrmNote}
                        campaignName={selectedCampaign?.name}
                        exportEnabled={!!entitlement?.export_enabled}
                      />

                      <PerfHubCreatorIntelligence
                        intelligence={dash?.creator_intelligence}
                        crmList={crm}
                        busy={busy}
                        formatNumber={formatHubNumber}
                        selectedRowIds={creatorRowSelection}
                        onToggleRowSelect={toggleCreatorRowSelection}
                        onSelectAllVisible={selectCreatorRowIds}
                        onClearSelection={clearCreatorRowSelection}
                        onBulkShortlist={openBulkShortlist}
                        onOpenShortlist={(c) => setShortlistModalCreators([c])}
                        campaignName={selectedCampaign?.name}
                        exportEnabled={!!entitlement?.export_enabled}
                      />

                      <PerfHubPostGuardPanel
                        postGuard={dash?.post_guard}
                        campaignName={selectedCampaign?.name}
                        exportEnabled={!!entitlement?.export_enabled}
                        canRefreshInstagram={
                          !!selectedId &&
                          selectedCampaign?.status === 'monitoring' &&
                          !selectedArchived
                        }
                        refreshBusy={postGuardRefreshBusy}
                        onRefreshInstagram={handlePostGuardInstagramRefresh}
                        onEditRules={
                          !selectedArchived ? () => setPostGuardModalOpen(true) : undefined
                        }
                      />
                    </>
                  )}

                  {!selectedId && (
                    <div className="empty-state" style={{ padding: '28px 16px' }}>
                      <span>📭</span>
                      <h3>No campaign selected</h3>
                      <p>Create a campaign under the Campaigns tab to see metrics.</p>
                    </div>
                  )}

                </section>

                <section className="section-card ph-glass-card">
                  <h3>
                    <span className="ph-section-icon">🏆</span>
                    Top performers
                    <PerfHubSectionInfo label="Top performers">
                      {PERF_HUB_OVERVIEW_HELP.topPerformers}
                    </PerfHubSectionInfo>
                  </h3>
                  <p className="ph-section-lead ph-card-lead-spacing">
                    Connected creators ranked on your latest metrics refresh—sort and add standouts
                    to your CRM shortlist.
                  </p>
                  {sortedTopPerformers.length > 0 && (
                    <>
                      <PerfHubCreatorSelectToolbar
                        visibleCount={sortedTopPerformers.length}
                        selectedCount={sortedTopPerformers.filter((tp) =>
                          creatorRowSelection.has(tp.row?.id)
                        ).length}
                        onSelectAll={() =>
                          selectCreatorRowIds(sortedTopPerformers.map((tp) => tp.row?.id))
                        }
                        onClear={clearCreatorRowSelection}
                        onBulkShortlist={openBulkShortlist}
                        busy={busy}
                      />
                      <div className="ph-top-performers-toolbar">
                        <label className="ph-top-performers-sort-label" htmlFor="ph-top-performers-sort">
                          Sort by
                        </label>
                        <select
                          id="ph-top-performers-sort"
                          className="ph-top-performers-sort"
                          value={topPerformersSort}
                          onChange={(e) => setTopPerformersSort(e.target.value)}
                        >
                          <option value="engagement">Engagement</option>
                          <option value="impressions">Impressions</option>
                          <option value="reach">Reach</option>
                          <option value="saved">Saves</option>
                          <option value="rebook">Re-book score</option>
                          <option value="ugc">UGC score</option>
                          {economicsConfigured && (
                            <>
                              <option value="spend">Spend</option>
                              <option value="cpe">CPE (lowest first)</option>
                            </>
                          )}
                          <option value="crm">Shortlist status</option>
                        </select>
                        <button
                          type="button"
                          className="btn-outline ph-top-performers-download-btn"
                          disabled={sortedTopPerformers.length === 0}
                          onClick={downloadFilteredTopPerformersCsv}
                          title="Download rows shown in the table (current sort)"
                        >
                          Download filtered CSV
                        </button>
                      </div>
                    </>
                  )}
                  {!sortedTopPerformers.length && (
                    <div className="empty-state">
                      <span>📊</span>
                      <h3>No synced posts yet</h3>
                      <p>
                        Add creators (CSV or manual) from the Campaigns tab and use Refresh Instagram data
                        to populate this leaderboard.
                      </p>
                    </div>
                  )}
                  {sortedTopPerformers.length > 0 && (
                    <div className="ph-table-wrap ph-top-performers-table-wrap">
                      <table className="funds-table enhanced ph-top-performers-table">
                        <thead>
                          <tr>
                            <th className="ph-th-check" aria-label="Select" />
                            <th>Creator</th>
                            <th>Post</th>
                            <th>Status</th>
                            <th>Engagement</th>
                            <th>Impressions</th>
                            <th>Reach</th>
                            <th title="Re-book score">Re-book</th>
                            <th title="UGC score">UGC</th>
                            {economicsConfigured && (
                              <>
                                <th>Spend</th>
                                <th title="Cost per 1,000 reach">₹ / 1K reach</th>
                                <th title="Cost per engagement">CPE</th>
                              </>
                            )}
                            <th title="Saved shortlist intents">Shortlist</th>
                            <th className="ph-th-actions" aria-label="Actions" />
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTopPerformers.map((tp) => {
                            const rowId = tp.row?.id;
                            const rowSelected = rowId && creatorRowSelection.has(rowId);
                            const intel = tp.intel || intelByRowId[rowId];
                            const crmEntry = getCrmEntryForHandle(crm, tp.row?.instagram_handle);
                            const saved = crmEntryShortlisted(crmEntry);
                            return (
                              <tr
                                key={rowId}
                                className={rowSelected ? 'ph-creator-row--selected' : ''}
                              >
                                <td className="ph-td-check">
                                  <input
                                    type="checkbox"
                                    className="ph-creator-row-check"
                                    checked={Boolean(rowSelected)}
                                    disabled={busy || !rowId}
                                    aria-label={`Select @${tp.row?.instagram_handle}`}
                                    onChange={() => toggleCreatorRowSelection(rowId)}
                                  />
                                </td>
                                <td>
                                  <strong style={{ color: '#1f2937' }}>
                                    @{tp.row?.instagram_handle}
                                  </strong>
                                </td>
                                <td className="ph-td-post-link">
                                  {tp.postLink ? (
                                    <a
                                      href={tp.postLink}
                                      className="ph-inline-link"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      View post
                                    </a>
                                  ) : (
                                    <span className="ph-cell-muted">—</span>
                                  )}
                                </td>
                                <td>
                                  <StatusPill status={tp.row?.invite_status} />
                                </td>
                                <td>
                                  <span
                                    className="ph-score"
                                    title={formatHubNumber(tp.engagement)}
                                  >
                                    {formatHubNumberCompact(tp.engagement)}
                                  </span>
                                </td>
                                <td title={formatHubNumber(tp.impressions)}>
                                  {formatHubNumberCompact(tp.impressions)}
                                </td>
                                <td title={formatHubNumber(tp.reach)}>
                                  {formatHubNumberCompact(tp.reach)}
                                </td>
                                <td>
                                  <span className="ph-intel-score">{intel?.rebook_score ?? '—'}</span>
                                </td>
                                <td>
                                  <span className="ph-intel-score ph-intel-score--ugc">
                                    {intel?.ugc_score ?? '—'}
                                  </span>
                                  {intel?.ugc_suggested && (
                                    <span className="ph-intel-suggested-label">Suggested</span>
                                  )}
                                </td>
                                {economicsConfigured && (
                                  <>
                                    <td>{formatInr(tp.economics?.spend_inr)}</td>
                                    <td>{formatInr(tp.economics?.cost_per_1k_reach_inr)}</td>
                                    <td>{formatInr(tp.economics?.cpe_engagement_inr)}</td>
                                  </>
                                )}
                                <td className="ph-top-performers-shortlist-status">
                                  {formatTopPerformerShortlistStatus(crmEntry)}
                                </td>
                                <td className="ph-td-actions">
                                  <button
                                    type="button"
                                    className="btn-outline ph-btn-crm-quick"
                                    disabled={busy || !tp.row?.id}
                                    onClick={() => openShortlistForRow(tp.row)}
                                  >
                                    {saved ? 'Update shortlist' : 'Add to shortlist'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              </>
            )}

            {displayTab === 'campaigns' && (
              <>
                <div className="ph-subtabs" role="tablist" aria-label="Campaign lifecycle">
                  {CAMPAIGN_SUB_TABS.map((st) => (
                    <button
                      key={st.id}
                      type="button"
                      role="tab"
                      aria-selected={campaignSubTab === st.id}
                      className={`ph-subtab ${campaignSubTab === st.id ? 'active' : ''}`}
                      onClick={() => {
                        if (st.id === 'setup') {
                          resetCreateSetupForm();
                        }
                        setCampaignSubTab(st.id);
                      }}
                    >
                      <span className="ph-subtab-icon" aria-hidden>
                        {st.icon}
                      </span>
                      {st.label}
                      <span className="ph-subtab-badge">{subTabCounts[st.id] ?? 0}</span>
                    </button>
                  ))}
                </div>

                <section className="section-card ph-glass-card ph-section-card">
                  {campaignSubTab === 'setup' ? (
                    <h3>
                      <span className="ph-section-icon">📁</span>
                      Create & Setup
                    </h3>
                  ) : (
                    <h3 className="ph-sr-only">
                      {campaignSubTab === 'live' && 'Live campaigns'}
                      {campaignSubTab === 'completed' && 'Completed & archived campaigns'}
                      {campaignSubTab === 'cancelled' && 'Cancelled campaigns'}
                    </h3>
                  )}
                  <div className="ph-campaign-name-block">
                    {showNewListWizard && (
                      <div className="ph-wizard-grid ph-campaign-name-grid">
                        <div className="ph-wizard-field">
                          <label className="ph-field-label" htmlFor="ph-new-name">
                            Campaign name
                          </label>
                          <input
                            id="ph-new-name"
                            className="ph-input ph-input-full"
                            placeholder="e.g. Q1 ambassador posts"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            autoComplete="off"
                          />
                        </div>
                        <div className="ph-wizard-field ph-wizard-field-narrow">
                          <label className="ph-field-label" htmlFor="ph-monitor-unit-create">
                            Track using
                          </label>
                          <select
                            id="ph-monitor-unit-create"
                            className="ph-input"
                            value={createMonitoringUnit}
                            onChange={(e) => {
                              const next = e.target.value;
                              const committed = clampPerfHubMonitoringDraft(
                                createMonitoringUnit,
                                perfHubMonitorCountDraft
                              );
                              const daysAfter =
                                createMonitoringUnit === 'days' ? committed : createMonitoringDays;
                              const weeksAfter =
                                createMonitoringUnit === 'weeks' ? committed : createMonitoringWeeks;
                              if (createMonitoringUnit === 'days') {
                                setCreateMonitoringDays(committed);
                              } else {
                                setCreateMonitoringWeeks(committed);
                              }
                              setCreateMonitoringUnit(next);
                              setPerfHubMonitorCountDraft(
                                String(next === 'days' ? daysAfter : weeksAfter)
                              );
                            }}
                          >
                            <option value="weeks">Weeks (1–4)</option>
                            <option value="days">Days (1–30)</option>
                          </select>
                        </div>
                        <div className="ph-wizard-field ph-wizard-field-narrow">
                          <label className="ph-field-label" htmlFor="ph-monitor-count-create">
                            {createMonitoringUnit === 'days' ? 'Number of days' : 'Number of weeks'}
                          </label>
                          <input
                            id="ph-monitor-count-create"
                            className="ph-input"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            placeholder={createMonitoringUnit === 'days' ? '1–30' : '1–4'}
                            aria-label={
                              createMonitoringUnit === 'days'
                                ? 'Number of days (1 to 30)'
                                : 'Number of weeks (1 to 4)'
                            }
                            value={perfHubMonitorCountDraft}
                            onChange={(e) => {
                              const t = e.target.value;
                              if (t !== '' && !/^\d*$/.test(t)) return;
                              const max = createMonitoringUnit === 'days' ? 30 : 4;
                              if (t === '') {
                                setPerfHubMonitorCountDraft('');
                                return;
                              }
                              const n = parseInt(t, 10);
                              if (!Number.isNaN(n) && n > max) {
                                setPerfHubMonitorCountDraft(String(max));
                                return;
                              }
                              setPerfHubMonitorCountDraft(t);
                            }}
                            onBlur={() => {
                              const v = clampPerfHubMonitoringDraft(
                                createMonitoringUnit,
                                perfHubMonitorCountDraft
                              );
                              setPerfHubMonitorCountDraft(String(v));
                              if (createMonitoringUnit === 'days') {
                                setCreateMonitoringDays(v);
                              } else {
                                setCreateMonitoringWeeks(v);
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {['live', 'completed', 'cancelled'].includes(campaignSubTab) &&
                    filteredCampaigns.length > 0 && (
                      <div
                        className="ph-live-campaign-stack"
                        role="list"
                        aria-label={
                          campaignSubTab === 'live'
                            ? 'Live campaigns'
                            : campaignSubTab === 'completed'
                            ? 'Completed campaigns'
                            : 'Cancelled campaigns'
                        }
                      >
                        {filteredCampaigns.map((c) => {
                          const snap = campaignMetricsCache[c.id];
                          const panelRows = campaignPanelRowsById[c.id];
                          const creatorsOpen = !!campaignPanelCreatorsOpenById[c.id];
                          const isLiveMonitoring = c.status === 'monitoring';
                          const invitesEnabled = snap?.capabilities?.creator_invites_enabled === true;
                          return (
                            <article key={c.id} className="ph-live-campaign-panel" role="listitem">
                              <header className="ph-live-panel-head">
                                <div className="ph-live-panel-title-block">
                                  <h4 className="ph-live-panel-title">{c.name || 'Untitled campaign'}</h4>
                                  <p className="ph-live-panel-meta ph-cell-muted">
                                    <StatusPill status={c.status} />
                                    {formatPhMonitoringWindowLabel(c) && (
                                      <>
                                        {' '}
                                        · {formatPhMonitoringWindowLabel(c)} window
                                      </>
                                    )}
                                    {c.monitoring_ends_at && isLiveMonitoring && (
                                      <>
                                        {' '}
                                        · <strong>Runs until:</strong>{' '}
                                        {new Date(c.monitoring_ends_at).toLocaleString()}
                                      </>
                                    )}
                                    {c.completed_at && (
                                      <>
                                        {' '}
                                        · <strong>Completed:</strong>{' '}
                                        {new Date(c.completed_at).toLocaleString()}
                                      </>
                                    )}
                                    {c.cancelled_at && (
                                      <>
                                        {' '}
                                        · <strong>Cancelled:</strong>{' '}
                                        {new Date(c.cancelled_at).toLocaleString()}
                                      </>
                                    )}
                                  </p>
                                </div>
                                <div className="ph-live-panel-actions">
                                  <button
                                    type="button"
                                    className="btn-primary ph-btn-glow ph-live-panel-refresh"
                                    disabled={busy || panelMetricsRefreshBusyIds[c.id]}
                                    onClick={() => handlePanelMetricsRefresh(c.id, c.status)}
                                  >
                                    {panelMetricsRefreshBusyIds[c.id]
                                      ? 'Refreshing Metrics…'
                                      : 'Refresh Metrics'}
                                  </button>
                                  {isLiveMonitoring && (
                                    <button
                                      type="button"
                                      className="btn-outline ph-live-panel-cancel"
                                      style={{ borderColor: '#b91c1c', color: '#b91c1c' }}
                                      disabled={busy || panelMetricsRefreshBusyIds[c.id]}
                                      onClick={() => handleCancelCampaignFlow(c.id)}
                                    >
                                      Cancel Campaign
                                    </button>
                                  )}
                                  {isLiveMonitoring && (
                                    <p className="ph-live-cancel-hint ph-cell-muted">
                                      Within {cancelGraceHours}h of go-live, cancel frees a workspace slot; after that,
                                      tracking stops but the slot stays used.
                                    </p>
                                  )}
                                </div>
                              </header>
                              <div className="ph-live-panel-body">
                                {snap?.error ? (
                                  <p className="ph-cell-muted">Could not load metrics for this campaign.</p>
                                ) : snap?.totals ? (
                                  <>
                                    <PerfHubTotalsGrid
                                      totals={snap.totals}
                                      availability={snap.totals_availability}
                                      gridClassName="ph-stats-grid-live"
                                    />
                                    {snap?.economics && (
                                      <PerfHubCampaignEconomics
                                        economics={snap.economics}
                                        variant="compact"
                                        showEdit={false}
                                      />
                                    )}
                                    <PerfHubInsightsBlock insights={snap.insights} />
                                  </>
                                ) : (
                                  <p className="ph-cell-muted">Loading Metrics…</p>
                                )}
                              </div>
                              <div className="ph-live-creators-nested">
                                <button
                                  type="button"
                                  className="ph-creators-toggle"
                                  aria-expanded={creatorsOpen}
                                  onClick={() =>
                                    setCampaignPanelCreatorsOpenById((prev) => ({
                                      ...prev,
                                      [c.id]: !prev[c.id],
                                    }))
                                  }
                                >
                                  <span className="ph-creators-toggle-chevron" aria-hidden>
                                    {creatorsOpen ? '▼' : '▶'}
                                  </span>
                                  <span className="ph-creators-toggle-label">
                                    Creators ({Array.isArray(panelRows) ? panelRows.length : '—'})
                                    {!creatorsOpen
                                      ? isLiveMonitoring && invitesEnabled
                                        ? ' — show rows, emails & resend'
                                        : ' — show rows & emails'
                                      : ''}
                                  </span>
                                </button>
                                {creatorsOpen && (
                                  <>
                                    {!Array.isArray(panelRows) ? (
                                      <p className="ph-cell-muted ph-live-creators-loading">
                                        Loading creators…
                                      </p>
                                    ) : panelRows.length === 0 ? (
                                      <div className="empty-state ph-live-creators-empty">
                                        <span>📋</span>
                                        <h3>No rows yet</h3>
                                        <p>Add creators before going live, or wait for processing.</p>
                                      </div>
                                    ) : (
                                      <div className="ph-table-wrap">
                                        <table className="funds-table enhanced">
                                          <thead>
                                            <tr>
                                              <th>Handle</th>
                                              <th>Email</th>
                                              <th>Status</th>
                                              <th style={{ width: 120 }} />
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {panelRows.map((r) => (
                                              <tr key={r.id}>
                                                <td>
                                                  <strong style={{ color: '#1f2937' }}>
                                                    @{r.instagram_handle}
                                                  </strong>
                                                </td>
                                                <td>
                                                  {r.email || <span className="ph-cell-muted">—</span>}
                                                </td>
                                                <td>
                                                  <StatusPill status={r.invite_status} />
                                                </td>
                                                <td>
                                                  {invitesEnabled &&
                                                    r.invite_status !== 'connected' &&
                                                    isLiveMonitoring && (
                                                      <button
                                                        type="button"
                                                        className="btn-outline"
                                                        disabled={busy}
                                                        onClick={() => handleResend(r, c.id)}
                                                      >
                                                        Resend
                                                      </button>
                                                    )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    )}

                  {['live', 'completed', 'cancelled'].includes(campaignSubTab) &&
                    filteredCampaigns.length === 0 &&
                    !showNewListWizard && (
                      <div className="ph-bucket-empty" role="status">
                        {campaignSubTab === 'live' && (
                          <div className="ph-bucket-empty ph-bucket-empty--live">
                            <span className="ph-bucket-empty-icon" aria-hidden>
                              📭
                            </span>
                            <h4 className="ph-bucket-empty-title">No live campaigns yet</h4>
                            <p className="ph-bucket-empty-copy">
                              Open <strong>Create &amp; Setup</strong>, add creators, then use{' '}
                              <strong>Create campaign &amp; send invite emails</strong>. Metrics and panels appear here after
                              monitoring starts.
                            </p>
                          </div>
                        )}
                        {campaignSubTab === 'completed' && (
                          <div className="ph-bucket-empty ph-bucket-empty--muted">
                            <span className="ph-bucket-empty-icon" aria-hidden>
                              ✅
                            </span>
                            <h4 className="ph-bucket-empty-title">No completed campaigns</h4>
                            <p className="ph-bucket-empty-copy">
                              Finished or archived campaigns show here when the monitoring window ends or you close a
                              campaign.
                            </p>
                          </div>
                        )}
                        {campaignSubTab === 'cancelled' && (
                          <div className="ph-bucket-empty ph-bucket-empty--muted">
                            <span className="ph-bucket-empty-icon" aria-hidden>
                              ⛔
                            </span>
                            <h4 className="ph-bucket-empty-title">No cancelled campaigns</h4>
                            <p className="ph-bucket-empty-copy">
                              Campaigns you stop early from the <strong>Live</strong> tab appear here for reference.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                  {campaignSubTab === 'setup' && atActiveCampaignLimit && (
                    <div className="ph-at-limit-block">
                      <div className="alert-banner info ph-insights-banner" role="status" style={{ marginTop: 16 }}>
                        <span className="alert-icon">📋</span>
                        <div className="alert-content">
                          <p style={{ margin: 0, lineHeight: 1.5 }}>
                            {filteredCampaigns.length > 0 ? (
                              <>
                                Your plan allows <strong>{maxActiveCampaigns}</strong> total campaign
                                {maxActiveCampaigns !== 1 ? 's' : ''} at once (draft + live, plus live lists you
                                cancelled after the first {cancelGraceHours} hours). <strong>Draft</strong> and{' '}
                                <strong>live</strong> both count toward the cap (Live can still show 0).
                              </>
                            ) : (
                              <>
                                You are at your limit (<strong>{activeCampaignCount}</strong> /{' '}
                                <strong>{maxActiveCampaigns}</strong> slots). Delete a draft, or from{' '}
                                <strong>Live</strong> cancel within {cancelGraceHours}h of going live to free a slot —
                                otherwise see{' '}
                              </>
                            )}
                            {filteredCampaigns.length === 0 && (
                              <button
                                type="button"
                                className="ph-inline-link"
                                onClick={() => {
                                  setTab('plans');
                                  setError('');
                                }}
                              >
                                Plans &amp; Pricing
                              </button>
                            )}
                            {filteredCampaigns.length === 0 && '.'}
                            {filteredCampaigns.length > 0 && (
                              <>
                                {' '}
                                Delete a draft below, cancel a live list from <strong>Live</strong> (within{' '}
                                {cancelGraceHours}h of go-live to free a slot), or see{' '}
                                <button
                                  type="button"
                                  className="ph-inline-link"
                                  onClick={() => {
                                    setTab('plans');
                                    setError('');
                                  }}
                                >
                                  Plans &amp; Pricing
                                </button>
                                .
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      {filteredCampaigns.length > 0 && (
                        <div className="ph-draft-slot-list" role="region" aria-label="Draft campaigns using your plan slot">
                          <p className="ph-cell-muted ph-draft-slot-lead">
                            Draft{filteredCampaigns.length !== 1 ? 's' : ''} on your account ({filteredCampaigns.length}
                            ) · live campaigns: {monitoringCampaignCount}
                          </p>
                          <ul className="ph-draft-slot-items">
                            {filteredCampaigns.map((c) => (
                              <li key={c.id} className="ph-draft-slot-row">
                                <span className="ph-draft-slot-name">{c.name || 'Untitled campaign'}</span>
                                <button
                                  type="button"
                                  className="btn-outline ph-btn-delete-draft"
                                  disabled={busy}
                                  onClick={() => handleDeletePerfHubCampaign(c.id)}
                                >
                                  Delete campaign permanently
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {showNewListWizard && (
                    <div className="ph-new-list-wizard">
                      <p className="ph-wizard-lead">
                        Add creators below. We create the campaign, import rows, send magic-link emails, and start
                        tracking in <strong>one step</strong>.
                      </p>

                      <div className="ph-creator-setup-card">
                        <div className="ph-creator-setup-head">
                          <span className="ph-creator-setup-icon" aria-hidden>
                            👥
                          </span>
                          <div>
                            <h4 className="ph-creator-setup-title">Creators</h4>
                            <p className="ph-creator-setup-sub">
                              CSV columns: <code>instagram_handle</code>, <code>email</code>,{' '}
                              <code>post_url</code> (required). Optional:{' '}
                              <code>creator_cost</code> or <code>fee_inr</code> (INR per creator for ROI).
                              Headers are case-insensitive — or add rows manually below.
                            </p>
                          </div>
                        </div>

                        <div className="ph-dropzone">
                          <div className="ph-dropzone-label">
                            <strong>Upload CSV</strong>
                            <span className="ph-dropzone-hint">
                              One row per creator · UTF-8 · max {PERF_HUB_CSV_MAX_BYTES / (1024 * 1024)} MB
                            </span>
                          </div>
                          <div className="ph-dropzone-file-row">
                            <input
                              key={csvInputKey}
                              id="ph-wizard-csv-input"
                              type="file"
                              accept=".csv,text/csv"
                              className="ph-file-input-sr"
                              onChange={handleWizardCsv}
                              disabled={busy}
                            />
                            <label htmlFor="ph-wizard-csv-input" className="btn-outline ph-file-pick-label">
                              Choose CSV file
                            </label>
                          </div>
                          {pendingCsvFile && (
                            <span className="ph-file-pill">
                              {pendingCsvFile.name}
                              <button
                                type="button"
                                className="ph-file-pill-x"
                                aria-label="Remove file"
                                onClick={() => {
                                  setPendingCsvFile(null);
                                  setCsvInputKey((k) => k + 1);
                                }}
                              >
                                ×
                              </button>
                            </span>
                          )}
                        </div>

                        <p className="ph-manual-divider">
                          <span>or add manually</span>
                        </p>

                        <form onSubmit={handleAddWizardPendingRow} className="ph-manual-grid">
                          <div className="ph-manual-field">
                            <label className="ph-mini-label" htmlFor="ph-wizard-ig">
                              Instagram handle
                            </label>
                            <input
                              id="ph-wizard-ig"
                              className="ph-input"
                              placeholder="@creator"
                              value={wizardManual.instagram_handle}
                              onChange={(e) => setWizardManual({ ...wizardManual, instagram_handle: e.target.value })}
                              autoComplete="off"
                            />
                          </div>
                          <div className="ph-manual-field">
                            <label className="ph-mini-label" htmlFor="ph-wizard-email">
                              Email
                            </label>
                            <input
                              id="ph-wizard-email"
                              className="ph-input"
                              type="email"
                              placeholder="creator@email.com"
                              value={wizardManual.email}
                              onChange={(e) => setWizardManual({ ...wizardManual, email: e.target.value })}
                              autoComplete="off"
                            />
                          </div>
                          <div className="ph-manual-field ph-manual-field-grow">
                            <label className="ph-mini-label" htmlFor="ph-wizard-url">
                              Post URL
                            </label>
                            <input
                              id="ph-wizard-url"
                              className="ph-input"
                              placeholder="https://www.instagram.com/p/…"
                              value={wizardManual.post_url}
                              onChange={(e) => setWizardManual({ ...wizardManual, post_url: e.target.value })}
                              autoComplete="off"
                            />
                          </div>
                          <div className="ph-manual-field">
                            <label className="ph-mini-label" htmlFor="ph-wizard-fee">
                              Creator cost (INR){' '}
                              <span className="ph-optional-label">optional</span>
                            </label>
                            <input
                              id="ph-wizard-fee"
                              className="ph-input"
                              inputMode="decimal"
                              placeholder="e.g. 5000"
                              value={wizardManual.fee_inr}
                              onChange={(e) => setWizardManual({ ...wizardManual, fee_inr: e.target.value })}
                              autoComplete="off"
                            />
                          </div>
                          <div className="ph-manual-field ph-manual-field-action">
                            <span className="ph-mini-label ph-mini-label-spacer" aria-hidden>
                              &nbsp;
                            </span>
                            <button type="submit" className="btn-outline ph-btn-add-row" disabled={busy}>
                              Add to campaign
                            </button>
                          </div>
                        </form>
                      </div>

                      <div className="ph-wizard-cta">
                        <button
                          type="button"
                          className="btn-primary ph-btn-glow"
                          disabled={busy}
                          onClick={handleCreateAndLaunch}
                        >
                          Create campaign &amp; send invite emails
                        </button>
                        <p className="ph-code-hint ph-wizard-cta-hint">
                          After you go <strong>live</strong>, you can cancel anytime: within the first{' '}
                          <strong>{cancelGraceHours} hours</strong> a cancel frees a workspace campaign slot; after that,
                          cancel still stops tracking but the slot stays used until your plan renews or you upgrade. The
                          monitoring window still ends automatically when scheduled.
                        </p>
                      </div>
                    </div>
                  )}

                </section>

                {campaignSubTab === 'setup' && (
                <section className="section-card ph-glass-card ph-section-card">
                  <h3>
                    <span className="ph-section-icon">👤</span>
                    Creators
                  </h3>
                  {campaignSubTab === 'setup' && !showNewListWizard && (
                    <div className="empty-state">
                      <span>📋</span>
                      <h3>New campaigns on hold</h3>
                      <p>
                        You are at your campaign limit. Free a slot on <strong>Live</strong> or upgrade under{' '}
                        <strong>Plans &amp; Pricing</strong>, then return here to stage creators.
                      </p>
                    </div>
                  )}
                  {campaignSubTab === 'setup' && showNewListWizard && (
                    <>
                      {pendingCreatorRows.length === 0 && !pendingCsvFile && (
                        <div className="empty-state">
                          <span>📋</span>
                          <h3>No creators yet</h3>
                          <p>Upload a CSV or add rows with the form above — they will show here before you go live.</p>
                        </div>
                      )}
                      {(pendingCreatorRows.length > 0 || pendingCsvFile) && (
                        <div className="ph-table-wrap">
                          {pendingCsvFile && (
                            <p className="ph-cell-muted" style={{ margin: '0 0 12px' }}>
                              CSV queued: <strong>{pendingCsvFile.name}</strong> — imported when you create the campaign.
                            </p>
                          )}
                          {pendingCreatorRows.length > 0 && (
                            <table className="funds-table enhanced">
                              <thead>
                                <tr>
                                  <th>Handle</th>
                                  <th>Email</th>
                                  <th>Post URL</th>
                                  <th>Cost (INR)</th>
                                  <th style={{ width: 100 }} />
                                </tr>
                              </thead>
                              <tbody>
                                {pendingCreatorRows.map((row, i) => (
                                  <tr key={`${row.instagram_handle}-${row.email}-${i}`}>
                                    <td>
                                      <strong style={{ color: '#1f2937' }}>@{row.instagram_handle}</strong>
                                    </td>
                                    <td>{row.email || <span className="ph-cell-muted">—</span>}</td>
                                    <td>
                                      {row.post_url ? (
                                        <a href={row.post_url} className="ph-inline-link" target="_blank" rel="noreferrer">
                                          Link
                                        </a>
                                      ) : (
                                        <span className="ph-cell-muted">—</span>
                                      )}
                                    </td>
                                    <td>
                                      {row.fee_inr != null && row.fee_inr !== '' ? (
                                        formatInr(row.fee_inr)
                                      ) : (
                                        <span className="ph-cell-muted">—</span>
                                      )}
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn-outline"
                                        disabled={busy}
                                        onClick={() =>
                                          setPendingCreatorRows((rows) => rows.filter((_, j) => j !== i))
                                        }
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                      <div className="ph-clear-campaign-wrap">
                        <button
                          type="button"
                          className="btn-outline"
                          disabled={busy}
                          onClick={resetCreateSetupForm}
                        >
                          Clear campaign
                        </button>
                        <p className="ph-cell-muted ph-clear-campaign-hint">
                          Resets name, monitoring window, queued CSV, and all staged creators.
                        </p>
                      </div>
                    </>
                  )}
                </section>
                )}
              </>
            )}

            {displayTab === 'crm' && (
              <PerfHubCrmTab
                crm={crm}
                busy={busy}
                crmForm={crmForm}
                setCrmForm={setCrmForm}
                campaigns={campaigns}
                onSave={handleCrmSave}
                onRemove={handleCrmRemove}
                exportEnabled={!!entitlement?.export_enabled}
              />
            )}

            {displayTab === 'plans' && (
              <>
                {!phCheckoutEnabled && (
                  <div className="ph-alert-warning" role="status" style={{ marginBottom: '1rem' }}>
                    <strong>Paid checkout is paused.</strong> Free Performance Hub stays available. For Pro or
                    Enterprise, contact support—we will enable self-serve checkout again when ready.
                  </div>
                )}
                <div className="ph-plans-grid">
                  <div className={`ph-plan-card ${!isPro ? 'ph-plan-card-current' : ''}`}>
                    {!isPro && <span className="ph-plan-ribbon">Current</span>}
                    <h4 className="ph-plan-name">Free</h4>
                    <p className="ph-plan-price">
                      <span className="ph-plan-price-amount">₹0</span>
                      <span className="ph-plan-price-unit">forever</span>
                    </p>
                    <p className="ph-plan-hook">Try Performance Hub on a single campaign with a small creator set.</p>
                    <PerfHubPlanCompareBullets
                      maxCampaigns={PH_PLAN_DEFAULTS.free.maxCampaigns}
                      maxCreators={PH_PLAN_DEFAULTS.free.maxCreators}
                      csvExport={false}
                      crmIncluded={false}
                    />
                    <div className="ph-plan-purchase-row">
                      <button
                        type="button"
                        className={!isPro ? 'btn-secondary' : 'btn-outline'}
                        disabled
                      >
                        {!isPro ? 'Current plan' : 'Free — no payment required'}
                      </button>
                    </div>
                  </div>

                  {phPaidPlansDisplay.map((plan) => {
                    const isCurrentPlan = activePaidPlanKey === plan.key;
                    const renewalDate = isCurrentPlan
                      ? formatPerfHubRenewalDate(entitlement?.current_period_end)
                      : '';
                    const isUpgradeFromPro =
                      isPro && activePaidPlanKey === 'pro' && plan.key === 'enterprise';
                    return (
                    <div
                      key={plan.key}
                      className={`ph-plan-card ${
                        plan.key === 'enterprise' ? 'ph-plan-card-enterprise' : 'ph-plan-card-paid'
                      } ${isCurrentPlan ? 'ph-plan-card-current' : ''}`}
                    >
                      {isCurrentPlan && (
                        <span className="ph-plan-ribbon">Current</span>
                      )}
                      <span className="ph-plan-discount-pill">{Math.round(PH_LAUNCH_OFF_FRACTION * 100)}% off</span>
                      <h4 className="ph-plan-name">{plan.label}</h4>
                      <div className="ph-plan-price-block">
                        <p className="ph-plan-price">
                          <span className="ph-plan-price-amount">₹{formatHubNumber(plan.saleInr)}</span>
                          <span className="ph-plan-price-unit">/ month</span>
                        </p>
                        <p className="ph-plan-reference">
                          <span className="ph-plan-was">₹{formatHubNumber(plan.listInr)}</span>
                          <span className="ph-plan-reference-label">original price</span>
                        </p>
                      </div>
                      <p className="ph-plan-hook">
                        {plan.key === 'pro'
                          ? 'More concurrent campaigns and a larger connected roster for growing brands.'
                          : 'Higher caps for teams running several campaigns and many creators at once.'}
                      </p>
                      <PerfHubPlanCompareBullets
                        maxCampaigns={plan.maxCampaigns}
                        maxCreators={plan.maxCreators}
                        csvExport
                      />
                      <div className="ph-plan-purchase-row">
                        {isCurrentPlan ? (
                          <>
                            <button type="button" className="btn-secondary" disabled>
                              Current plan
                            </button>
                            {renewalDate && (
                              <p className="ph-plan-renews-note">
                                Renews / ends on <strong>{renewalDate}</strong>
                              </p>
                            )}
                            {renewalDate && billingPlanCard?.periodSubLine && (
                              <p className="ph-plan-renews-stacked ph-cell-muted">
                                Includes unused time from a previous checkout—you upgraded mid-period, so paid
                                access can span more than one month while limits update right away.
                              </p>
                            )}
                          </>
                        ) : isPro && perfHubEntitlementMeetsPlan(displayLimits, plan) ? (
                          <button type="button" className="btn-outline" disabled>
                            Included with your workspace
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn-primary"
                              disabled={!!planCheckoutTier || loading || !phCheckoutEnabled}
                              onClick={() => openPerfHubPlanCheckout(plan.key)}
                            >
                              {planCheckoutTier === plan.key
                                ? 'Opening checkout…'
                                : !phCheckoutEnabled
                                  ? 'Checkout unavailable'
                                  : isUpgradeFromPro
                                    ? 'Upgrade to Enterprise'
                                    : `Purchase ${plan.label}`}
                            </button>
                            {isUpgradeFromPro && (
                              <p className="ph-plan-upgrade-note">
                                Replaces your current Pro plan with Enterprise. We add a fresh
                                30-day Enterprise cycle on top of any time left on Pro — no
                                proration credit beyond that.
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>

                <section className="section-card ph-glass-card ph-section-card ph-plans-compare-note">
                  <h3>
                    <span className="ph-section-icon">ℹ️</span>
                    How billing works
                  </h3>
                  <p className="ph-section-lead ph-plans-billing-lead">
                    <strong>Free</strong>, <strong>Pro</strong>, and <strong>Enterprise Plan</strong> are described above.
                    Paid plans are billed <strong>monthly</strong> in rupees; <strong>yearly</strong> billing will be added
                    later. Each purchase activates a <strong>30-day</strong> cycle — when it ends and we do not record a
                    new payment, your workspace is automatically demoted back to <strong>Free</strong>. The strikethrough
                    amount is the <strong>original price</strong> used for the launch <strong>60% off</strong> offer.
                    Your workspace <strong>entitlement</strong> (set after purchase or by Milan admin) is what actually
                    enforces campaign and creator caps—if yours differ from the card, trust the entitlement.
                    {MILAN_WORKSPACE_ENABLED
                      ? ' This product is separate from Milan Workspace paid discovery campaigns.'
                      : ''}{' '}
                    Invoices or plan-change
                    events appear under <strong>Billing &amp; Usages</strong> when recorded.
                  </p>
                </section>
              </>
            )}

            {displayTab === 'billing' && (
              <div className="ph-billing-page">
                <div className="ph-billing-top-grid">
                  <section className="ph-billing-hero ph-glass-card ph-section-card">
                    {!billingPlanCard ? (
                      <p className="ph-cell-muted ph-billing-loading">Loading your plan…</p>
                    ) : (
                      <>
                        <div className="ph-billing-hero-top">
                          <span className="ph-billing-plan-pill">{billingPlanCard.name}</span>
                          {billingPlanCard.isAdminGrant && (
                            <span className="ph-billing-pill ph-billing-pill--muted">Admin</span>
                          )}
                        </div>
                        <div className="ph-billing-hero-body">
                          <div className="ph-billing-price-block">
                            {billingPlanCard.priceLine != null ? (
                              <>
                                <p className="ph-billing-price-amount">{billingPlanCard.priceLine}</p>
                                <p className="ph-billing-price-unit">{billingPlanCard.unit}</p>
                              </>
                            ) : (
                              <p className="ph-billing-price-fallback">{billingPlanCard.unit}</p>
                            )}
                          </div>
                          {billingPlanCard.periodLine && (
                            <p className="ph-billing-period">{billingPlanCard.periodLine}</p>
                          )}
                          {billingPlanCard.periodSubLine && (
                            <p className="ph-billing-period-note ph-cell-muted">{billingPlanCard.periodSubLine}</p>
                          )}
                        </div>
                        {billingUpgradeTargets.length > 0 && (
                          <div className="ph-billing-upgrade-row" role="group" aria-label="Upgrade options">
                            {!phCheckoutEnabled && (
                              <p className="ph-cell-muted ph-billing-checkout-paused">
                                Paid checkout is paused—contact support to change plan. Free tier is unchanged.
                              </p>
                            )}
                            {billingUpgradeTargets.map(({ tier, plan, isUpgrade }) => (
                              <div key={tier} className="ph-billing-upgrade-card">
                                <div className="ph-billing-upgrade-text">
                                  <strong>{isUpgrade ? `Upgrade to ${plan.label}` : plan.label}</strong>
                                  <span className="ph-billing-upgrade-meta">
                                    ₹{formatHubNumber(plan.saleInr)}/mo · {plan.maxCampaigns} live campaigns ·{' '}
                                    {formatHubNumber(plan.maxCreators)} creators
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="btn-primary ph-billing-upgrade-btn"
                                  disabled={!!planCheckoutTier || loading || !phCheckoutEnabled}
                                  onClick={() => openPerfHubPlanCheckout(tier)}
                                >
                                  {planCheckoutTier === tier
                                    ? 'Opening…'
                                    : !phCheckoutEnabled
                                      ? 'Unavailable'
                                      : isUpgrade
                                        ? 'Upgrade'
                                        : 'Choose plan'}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </section>

                  <section className="section-card ph-glass-card ph-section-card ph-billing-usage-card ph-billing-usage-aside">
                    <h3 className="ph-billing-section-title">
                      <span className="ph-section-icon" aria-hidden>
                        📈
                      </span>
                      Usage
                    </h3>
                    <p className="ph-section-lead ph-billing-usage-lead">
                      Live campaigns are in tracking. Cancelling live after the first {cancelGraceHours} hours still
                      counts a workspace slot until your plan renews. Creators are counted once when Instagram is
                      connected anywhere in Performance Hub.
                    </p>
                    <div className="ph-billing-usage-list">
                      <div className="ph-billing-usage-row">
                        <div className="ph-billing-usage-label">
                          <span>Live campaigns</span>
                          <span className="ph-billing-usage-count">
                            {formatHubNumber(liveCampaignCount)} / {formatHubNumber(maxActiveCampaigns)}
                          </span>
                        </div>
                        <div
                          className="ph-usage-track"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={Math.round(campaignUsagePct)}
                          aria-label="Live campaigns vs workspace cap"
                        >
                          <div
                            className="ph-usage-track-fill ph-usage-track-fill--campaigns"
                            style={{ width: `${campaignUsagePct}%` }}
                          />
                        </div>
                        <p className="ph-billing-usage-remain">
                          {Math.max(0, maxActiveCampaigns - liveCampaignCount)} live slots remaining
                        </p>
                      </div>
                      <div className="ph-billing-usage-row">
                        <div className="ph-billing-usage-label">
                          <span>Connected creators</span>
                          <span className="ph-billing-usage-count">
                            {formatHubNumber(connectedCreatorsUsed)} / {formatHubNumber(maxConnectedCreators)}
                          </span>
                        </div>
                        <div
                          className="ph-usage-track"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={Math.round(creatorsUsagePct)}
                          aria-label="Connected creators vs workspace cap"
                        >
                          <div
                            className="ph-usage-track-fill ph-usage-track-fill--creators"
                            style={{ width: `${creatorsUsagePct}%` }}
                          />
                        </div>
                        <p className="ph-billing-usage-remain">
                          {Math.max(0, maxConnectedCreators - connectedCreatorsUsed)} remaining
                        </p>
                      </div>
                    </div>
                  </section>
                </div>

                <section className="section-card ph-glass-card ph-section-card ph-billing-activity-card ph-billing-activity-compact">
                  <div className="ph-billing-activity-head">
                    <h3 className="ph-billing-section-title">
                      <span className="ph-section-icon" aria-hidden>
                        🧾
                      </span>
                      Recent activity
                    </h3>
                    {billingStatus === 'ok' && (
                      <button type="button" className="btn-outline ph-billing-refresh" onClick={() => loadBilling()}>
                        Refresh
                      </button>
                    )}
                  </div>
                  {billingStatus === 'error' ? (
                    <div className="ph-billing-error-block">
                      <p className="ph-cell-muted">
                        Billing history did not load. Check your connection or open{' '}
                        <button type="button" className="ph-link-as-button" onClick={() => setTab('plans')}>
                          Plans &amp; Pricing
                        </button>{' '}
                        for purchases.
                      </p>
                      <button type="button" className="btn-outline" onClick={() => loadBilling()}>
                        Retry
                      </button>
                    </div>
                  ) : billingStatus !== 'ok' ? (
                    <p className="ph-cell-muted ph-billing-loading-inline">Loading recent activity…</p>
                  ) : recentBillingEvents.length === 0 ? (
                    <p className="ph-cell-muted ph-billing-empty">
                      No recorded events yet. Purchases and plan changes will appear here after checkout completes.
                    </p>
                  ) : (
                    <ul className="ph-billing-timeline">
                      {recentBillingEvents.map((ev) => (
                        <li key={ev.id || `${ev.event_type}-${ev.created_at}`} className="ph-billing-timeline-item">
                          <span className="ph-billing-timeline-dot" aria-hidden />
                          <div className="ph-billing-timeline-body">
                            <div className="ph-billing-timeline-title-row">
                              <strong>{formatPhBillingEventTitle(ev.event_type)}</strong>
                              {ev.amount_inr != null && Number(ev.amount_inr) > 0 && (
                                <span className="ph-billing-timeline-amount">₹{formatHubNumber(ev.amount_inr)}</span>
                              )}
                            </div>
                            {ev.description && (
                              <p className="ph-billing-timeline-desc">{ev.description}</p>
                            )}
                            <time className="ph-billing-timeline-time" dateTime={ev.created_at || undefined}>
                              {formatPhBillingEventWhen(ev.created_at)}
                            </time>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <p className="ph-billing-footnote">
                  {MILAN_WORKSPACE_ENABLED
                    ? 'Performance Hub billing is separate from Milan Workspace campaign payments. '
                    : ''}
                  Full tier comparison:{' '}
                  <button type="button" className="ph-link-as-button" onClick={() => setTab('plans')}>
                    Plans &amp; Pricing
                  </button>
                  .
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
      {successMessage && (
        <div
          className="ph-toast ph-toast--success"
          role="status"
          aria-live="polite"
        >
          <span aria-hidden>✓</span>
          <div className="ph-toast-body">{successMessage}</div>
          <button
            type="button"
            className="ph-toast-dismiss"
            onClick={() => setSuccessMessage('')}
          >
            Dismiss
          </button>
        </div>
      )}
      <PerfHubEconomicsModal
        open={economicsModalOpen}
        onClose={() => !busy && setEconomicsModalOpen(false)}
        onSave={handleSaveEconomics}
        busy={busy}
        campaignName={campaigns.find((c) => c.id === selectedId)?.name}
        initialBudget={
          dash?.economics?.summary?.campaign_budget_inr ??
          dash?.campaign?.economics_budget_inr
        }
        rows={dash?.rows?.length ? dash.rows : rows}
      />
      <PerfHubShortlistModal
        open={Boolean(shortlistModalCreators?.length)}
        onClose={() => !busy && setShortlistModalCreators(null)}
        creators={shortlistModalCreators || []}
        campaignName={selectedCampaign?.name}
        crmList={crm}
        busy={busy}
        onSave={handleShortlistSave}
      />
      <PerfHubPostGuardBriefModal
        open={postGuardModalOpen}
        onClose={() => !busy && setPostGuardModalOpen(false)}
        onSave={handleSavePostGuardBrief}
        busy={busy}
        campaignName={selectedCampaign?.name}
        initialBrief={dash?.post_guard?.brief || dash?.campaign?.post_guard_brief}
      />
      <Footer />
    </div>
  );
}

export default PerformanceHub;
